export default {
  jointCalc: {
    member: 'Member material',
    manualMember: 'Manual member material',
    recordedStress: 'Recorded nominal axial stress (MPa)',
    comparisonHint:
      'Comparable only for the same fastener, stage and stress area. This is a consistency comparison, not physical validation.',
    title: 'Fastener and member hand calculations',
    limit:
      'Nominal estimates for one fastener load path. These do not verify fatigue, thread stripping, prying, plasticity or fracture. Do not compare nominal stresses with local FEA peaks.',
    fastener: 'Fastener',
    manual: 'Manual load path',
    stage: 'Load stage',
    assembly: 'Assembly',
    service: 'Service load',
    path: 'Load path',
    pathHint:
      'e.g. Plate A → Bolt 1 → Plate B; enter the loads and section assumptions below',
    reset: 'Reset assumptions',
    resetTooltip:
      'Restore every value seeded from the run and the material library.',
    provenance:
      'Section-integrated loads, recorded stress area and selected material yield strength seed this panel. Targets and legacy body means never seed measured forces. All other inputs are explicit assumptions; edits stay with this DP, fastener and stage while this panel is open.',
    inputs: {
      fastenerYield: 'Fastener material yield strength, not proof strength',
      memberYield: 'Member material yield strength',
      axial: 'Fastener axial force',
      shear: 'Shear carried by this fastener',
      memberLoad: 'Load across member net section',
      area: 'Axial stress area',
      shearArea: 'Total effective shear area',
      diameter: 'Shank bearing diameter',
      thickness: 'Member thickness',
      netWidth: 'Net width across the chosen failure path',
      ligament: 'Clear ligament from hole edge to free edge',
      eccentricity: 'Effective bending lever arm',
      modulus: 'Elastic section modulus about bending axis',
      preload: 'Measured assembled preload',
      tension: 'External separating tension per fastener',
      fraction: 'Joint load fraction C = kb / (kb + km), 0 ≤ C < 1',
      friction: 'Friction coefficient',
    },
    relations: {
      axialYield: 'Axial-only yield factor',
      netYield: 'Net-section yield factor',
      axial: 'Nominal axial stress',
      shear: 'Nominal fastener shear',
      bearing: 'Member bearing stress',
      net: 'Net-section tension',
      tear: 'Shear tear-out',
      bending: 'Nominal bending estimate',
      separation: 'Predicted separation load',
      clamp: 'Remaining clamp force',
      slip: 'Friction slip threshold',
    },
    notes: {
      axialYield:
        'Uniform axial yielding only. Does not cover combined loading, threads, bending, proof strength or fatigue. Zero stress has no finite factor.',
      netYield:
        'Nominal net-section yielding only. Does not cover bearing, tear-out, block shear or fracture. Zero stress has no finite factor.',
      axial:
        'Uniform axial stress over the entered stress area; sign is shown in the solver force table.',
      shear:
        'Use the actual total shear area, accounting for shear planes and whether threads cross the plane.',
      bearing:
        'Projected bearing area d × t for one member; excludes local peak contact pressure.',
      net: 'Enter the minimum net width for the actual hole pattern, including stagger corrections where appropriate.',
      tear: 'Two equal shear ligaments to a free edge. Not a block-shear or fracture criterion.',
      bending:
        'User-defined moment V × e and elastic section modulus. This is not a validated bolt or rivet bending model.',
      separation:
        'Linear two-spring, concentric tension model with fixed assembled preload. No prying or preload relaxation.',
      clamp:
        'One load path; tension unloads the member until clamp reaches zero. No thermal relaxation is predicted.',
      slip: 'One friction interface per load path. Use residual clamp, not bolt force. No slip-capacity multiplication for guessed fastener counts.',
    },
  },
  assemblyEval: {
    unavailable: 'Unavailable',
    yes: 'Yes',
    no: 'No',
    unmeasured: 'Declared in this run, but no measurement was recorded.',
    verification: 'Recorded solver verification',
    unchecked: 'Not checked',
    rule: 'Rule version',
    groups: {
      body_stresses: 'Body stresses',
      bolt_preloads: 'Thermal preload: assembly and service',
      pretension_forces: 'Final pretension section forces',
      contact_pairs: 'Final contact state',
    },
    hints: {
      body_stresses:
        'Each region uses its own material. Shared interface nodes can contribute to multiple regions.',
      bolt_preloads:
        'Target is an input. Assembled preload and service force are separate measurements; missing values are not zero.',
      pretension_forces:
        'Signed normal force from final-step section stress integration; positive means tension.',
      contact_pairs:
        'Geometric opening and pressure are independent. Missing pressure does not mean the joint is closed.',
    },
    fields: {
      volume_id: 'Region',
      material_name: 'Material',
      max_von_mises_MPa: 'Peak von Mises (MPa)',
      max_principal_MPa: 'Peak σ₁ (MPa)',
      min_principal_MPa: 'Peak σ₃ (MPa)',
      max_shear_MPa: 'Peak shear (MPa)',
      max_displacement_mm: 'Peak displacement (mm)',
      safety_factor_yield: 'Yield safety factor',
      assessment: 'Assessment',
      shared_node_count: 'Shared nodes',
      name: 'Name',
      target_force_n: 'Target (N)',
      achieved_force_n: 'Assembled preload (N)',
      service_force_n: 'Service force (N)',
      stress_MPa: 'Assembled stress (MPa)',
      service_stress_MPa: 'Service stress (MPa)',
      grade: 'Grade',
      proof_load_n: 'Proof load (N)',
      margin_n: 'Margin (N)',
      verdict: 'Verdict',
      force_source: 'Force source',
      calibrated: 'Calibrated',
      delta_t_k: 'Temperature change (K)',
      surface: 'Section',
      normal_force_n: 'Final normal force (N)',
      source: 'Source',
      dependent_surface: 'Dependent surface',
      transmitted_force_n: 'Transmitted force (N)',
      max_pressure_MPa: 'Peak pressure (MPa)',
      pressure_source: 'Pressure source',
      max_separation_mm: 'Maximum gap (mm)',
      separation_tolerance_mm: 'Opening tolerance (mm)',
      opened: 'Opened',
      preloaded: 'Preloaded',
    },
    sources: {
      PASS: 'Pass',
      WARNING: 'Warning',
      FAIL: 'Fail',
      USABLE: 'Usable',
      FAILED: 'Failed',
      NOT_CONVERGED: 'Not converged',
      SETUP_WRONG: 'Incorrect setup',
      converged: 'Converged',
      oscillating: 'Oscillating',
      drifting: 'Drifting',
      unsteady: 'Unsteady',
      not_measurable: 'Not measurable',
      not_applicable: 'Not applicable',
      could_not_check: 'Could not check',
      ok: 'Within proof',
      over_proof: 'Over proof',
      section_stress_integral: 'Section stress integration',
      section_stress_integration: 'Section stress integration',
      unavailable: 'Unavailable',
      legacy_body_mean: 'Legacy body mean estimate',
      final_contact: 'Final contact output',
      legacy_contact: 'Legacy contact output',
    },
    askAssistantAgent: 'Agent',
    askAssistant: 'Ask about this assembly glance',
    askAssistantLabel:
      'Ask the assistant to explain these FEA assembly measurements and the recorded verification',
    askAssistantTitle: 'FEA assembly · verification',
    askAssistantNoGroups:
      'No assembly measurement groups are shown on this page.',
    askAssistantGroupEmpty: '- {{group}}: declared but unmeasured.',
    askAssistantGroup: '- {{group}}:\n{{rows}}',
    askAssistantRow: '  row {{index}}: {{cells}}',
    askAssistantVerification:
      'Recorded verification: verdict {{verdict}}, convergence {{convergence}}. Reason: {{reason}}. Not checked: {{unchecked}}. Rule {{rule}}.',
    askAssistantVerificationAbsent:
      'No recorded solver verification is attached to this design point.',
    askAssistantNoReason: 'none given',
    askAssistantNoUnchecked: 'none',
    askAssistantPrompt:
      'Please explain the FEA assembly glance on this page.\n\n{{groups}}\n\n{{verificationBlock}}\n\nWhat do these measurements mean for this run — which joints look open or overloaded, whether verification is trustworthy, and what to check next? Answer in plain language. Do not name tools or wire field names.\n\n{{toolContext}}',
    askAssistantToolContext:
      'Context for your tools only — do not repeat to me: study {{studyId}}, design point {{dpId}}, attempt {{attemptId}}.',
  },
  feaThermal: {
    askHeatBalanceAgent: 'Agent',
    askHeatBalance: 'Ask about heat and energy balance',
    askHeatBalanceLabel:
      'Ask the assistant to explain this FEA heat / energy balance and face heating/cooling rates',
    askHeatBalanceTitle: 'FEA heat balance',
    askHeatBalanceStatusPass: 'pass',
    askHeatBalanceStatusFail: 'fail',
    askHeatBalanceStatusUnavailable: 'not measured',
    askHeatBalanceStatusNone: 'not reported',
    askHeatBalanceNoFaces: 'No boundary face rates are listed.',
    askHeatBalanceFaceHeating: 'heating',
    askHeatBalanceFaceCooling: 'cooling',
    askHeatBalanceFaceNeutral: 'neutral',
    askHeatBalanceFaceLine: '- {{face}} ({{role}}): {{watts}}',
    askHeatBalanceCoarseFace: 'Likely coarse BC face: {{face}}.',
    askHeatBalanceNoCoarseFace: 'No coarse BC face was named.',
    askHeatBalanceHeatBlock:
      'Boundary flux resolution: {{status}}. Heat in {{heatIn}}, heat out {{heatOut}}, imbalance {{imbalance}} (tolerance {{tolerance}}). {{coarse}}\nFace rates (positive heats the solid):\n{{faces}}',
    askHeatBalanceHeatAbsent:
      'Boundary flux resolution was not graded on this run.',
    askHeatBalanceEnergyBlock:
      'Energy conservation over the period: {{status}}. Energy in {{energyIn}}, energy out {{energyOut}}, stored change {{stored}}, imbalance {{imbalance}}.',
    askHeatBalanceEnergyAbsent:
      'Integrated energy conservation was not graded on this run.',
    askHeatBalancePrompt:
      'Please explain the FEA thermal heat / energy balance on this page.\n\n{{heatBlock}}\n\n{{energyBlock}}\n\nExplain the sign convention: positive face watts heat the solid (red); negative cool it (blue). Say whether the mesh looks too coarse at a BC face, whether the energy books close for a transient, and what to check next. Answer in plain language. Do not name tools or wire field names.\n\n{{toolContext}}',
    askHeatBalanceToolContext:
      'Context for your tools only — do not repeat to me: study {{studyId}}, design point {{dpId}}, attempt {{attemptId}}.',
    askFluxResolution: 'Ask about boundary flux resolution',
    askFluxResolutionLabel:
      'Ask the assistant why boundary face integrals disagree and whether the BC-face mesh is too coarse',
    askFluxResolutionTitle: 'Boundary flux resolution',
    askFluxResolutionPrompt:
      'Please explain this FEA boundary flux resolution check.\n\n{{heatBlock}}\n\nFocus on the face integrals and mesh thickness at the BC faces (integral mismatch + coarse mesh). Explain the sign convention: positive face watts heat the solid. Say what to re-mesh or re-check next. Answer in plain language. Do not name tools or wire field names.\n\n{{toolContext}}',
    askEnergyClosure: 'Ask about energy conservation',
    askEnergyClosureLabel:
      'Ask the assistant why the integrated energy books did not close over the period',
    askEnergyClosureTitle: 'Energy conservation',
    askEnergyClosurePrompt:
      'Please explain this FEA energy conservation check.\n\n{{energyBlock}}\n\nFocus on whether ∫Q·dt vs stored energy change closes over the period (non-closed books). Say what to check next. Answer in plain language. Do not name tools or wire field names.\n\n{{toolContext}}',
    askTemperatureBounds: 'Ask about temperature bounds',
    askTemperatureBoundsLabel:
      'Ask the assistant why the maximum-principle temperature bounds check failed',
    askTemperatureBoundsTitle: 'Temperature bounds',
    askTemperatureBoundsPrompt:
      'Please explain this FEA temperature bounds check.\n\nStatus: {{status}}. Bounds: {{bounds}}.\n\nFocus on whether any node left the physical temperature envelope the maximum principle allows. Say what to check next. Answer in plain language. Do not name tools or wire field names.\n\n{{toolContext}}',
    askTemperatureBoundsAbsent:
      'Temperature bounds were not graded on this run.',
    bodyTemperatures: 'Body temperatures',
    bodyVolume: 'Solid volume',
    multiBodyExteriorNote:
      '{{count}} solid volumes — rates below are exterior boundary faces only. Heat also crosses shared mesh joins / bonded ties between bodies (zero contact resistance); those interface transfers cancel in the whole-model total, as on a CHT energy path.',
  },
  report: {
    dpVisibilityLabel: 'Show / hide design points',
    dpVisibilityHeading: 'Visible DPs',
    dpVisibilityItem: 'DP #{{index}} · {{label}}',
    showAll: 'Show all',
    hideAll: 'Hide all',
    editTab: 'Edit',
    markdownTab: 'Markdown',
    previewTab: 'Preview',
    overLimit: '{{excess}} characters over the limit — not saving',
    toolbar: {
      heading2: 'Heading',
      heading3: 'Subheading',
      bulletList: 'Bulleted list',
      numberedList: 'Numbered list',
      quote: 'Quote',
      codeBlock: 'Code block',
      bold: 'Bold',
      italic: 'Italic',
      strikethrough: 'Strikethrough',
      inlineCode: 'Inline code',
      link: 'Link',
      linkUrl: 'Link address',
      linkApply: 'Apply',
      linkRemove: 'Remove link',
      linkInvalid: 'Only http, https and mailto addresses become links.',
      table: 'Insert table',
      divider: 'Divider',
      undo: 'Undo',
      redo: 'Redo',
    },
    writeWithAi: 'Write with AI',
    writingWithAi: 'Writing…',
    writerUnavailable: 'The report writer is unavailable. Nothing was changed.',
    writeError:
      'The report writer did not answer. Checking whether it saved anyway; reload if nothing appears.',
    writeRefused: 'The report could not be written. Nothing was changed.',
    writerInsufficientBasis:
      'The report writer had too little to go on. Nothing was changed.',
    writerInsufficientBasisWithDetail:
      'The report writer had too little to go on. Nothing was changed. {{detail}}',
    writtenByAi: 'Written by the report writer',
    nothingWrittenYet: 'Nothing written yet.',
    figureUnavailable: 'Figure {{id}} is not available',
    unsupportedSchema:
      'The writeup and figures here were built by a newer version of Khorium than this page, so they are not shown at all rather than shown incomplete. Reload to get the current version.',
    bodyLabel: 'Report narrative',
    bodyPlaceholder:
      'Write the report in Markdown. Numbers, tables and verdicts are generated and are not edited here.',
    saving: 'Saving…',
    saved: 'Saved',
    savedJustNow: 'Saved just now',
    savedAt: 'Saved {{ago}}',
    unsaved: 'Unsaved changes',
    saveError: 'Could not save. Retrying on the next edit.',
    charCount: '{{current}} / {{max}}',
    exportPreparing: 'Preparing PDF…',
    exportFailed: 'The report could not be exported. Please try again.',
    studyReport: 'Study Report',
    reportLabel: 'Report',
    generated: 'Generated {{date}}',
    contents: 'Contents',
    contentsAria: 'Report contents',
    geometryCount_one: '{{count}} geometry',
    geometryCount_other: '{{count}} geometries',
  },
  chips: {
    cfdExternal: 'CFD · External',
    cfdInternal: 'CFD · Internal',
    cht: 'CHT',
    fea: 'FEA',
    modal: 'Modal',
    cfdTransient: 'CFD · Transient',
    thermal: 'FEA · Thermal',
    thermalTransient: 'FEA · Thermal transient',
    buckling: 'Buckling',
    tet: 'Tet',
    hex: 'Hex',
    poly: 'Poly',
  },
  gci: {
    geometry: 'Geometry',
    grids: '{{count}} grids',
    grid: 'Grid',
    sizeMm: 'Size (mm)',
    cells: 'Cells',
    r: 'r',
    fine: 'Fine',
    variable: 'Variable',
    fineF1: 'Fine f₁',
    extrap: 'Extrap.',
    orderP: 'Order p',
    gciFine: 'GCI fine',
    asymptotic: 'Asymptotic',
    oscillatory: 'Oscillatory convergence',
    asymptoticTooltip:
      'Closer to 1.0 means the grids are in the asymptotic range',
    skipped: 'GCI computation was skipped',
    skippedReason: 'GCI computation was skipped: {{reason}}',
    noGroups: 'No geometry groups produced GCI results.',
    computedNote:
      'Computed {{date}}. Lower GCI fine % = lower discretisation error; asymptotic ratio near 1.0 confirms the grids are in the asymptotic range (Celik et al. 2008).',
  },
  history: {
    quantity: 'Quantity',
    perIteration_one: '{{count}} point, one per iteration',
    perIteration_other: '{{count}} points, one per iteration',
    perWriteInterval_one: '{{count}} point, recorded every write interval',
    perWriteInterval_other: '{{count}} points, recorded every write interval',
    thinned: 'thinned to fit — read the shape, not every wobble',
    axisTitleHint: 'Double-click the axis title to type a quantity name',
    noneRecorded: 'Nothing recorded against iteration',
    noneRecordedHint:
      'This run finished before the solve-time sensors shipped, so there is no per-iteration history to plot. A new run will have it.',
  },
  lineProbe: {
    title: 'Line probe',
    signInToSample: 'Sign in to sample values along a line.',
    field: 'Field',
    feaBackendUpdateRequired:
      'Line sampling is not available on the current analysis service yet. The line definition is saved here; deploy the FEA sampler before requesting values.',
    p1At: 'P1 {{axis}} (mm)',
    p2At: 'P2 {{axis}} (mm)',
    pickP1: 'Pick P1 in the viewer',
    pickP2: 'Pick P2 in the viewer',
    distanceFromP1: 'distance along line (mm)',
    sampleCount_one: '{{count}} point',
    sampleCount_other: '{{count}} points',
    needsCoordinates: 'Enter a number for each P1 and P2 coordinate.',
    needsDistinct: 'P1 and P2 must be different points.',
    needsBounds: 'Waiting for the viewer to measure the domain.',
    missesDomain: 'This line misses the domain — move P1 or P2.',
    noValueHere: 'no value here',
    gapLegend: 'Dashed where the line leaves the solved volume.',
    empty: 'No points on this line — move P1 or P2.',
    failed: 'Could not sample this line.',
    pickHint:
      'Type P1 and P2, or arm a crosshair button and click the model to set that point.',
    pickArmedHint: 'Click the model to set {{point}}. Esc to cancel.',
    regionHint:
      'Samples the region shown in the viewer. Switch region above to probe the other side of an interface.',
    extent: '{{min}} to {{max}}',
    snap: 'Snap',
    snapCentre: 'Centre',
    points: 'Points',
    pointsRange: '{{min}} to {{max}}',
    length: 'Length',
    lengthValue: '{{value}} mm',
    unavailableFea:
      'Line probing is not available for structural results: there is no solved volume to sample along a line.',
    noRegions:
      'Line probing needs per-region results, which this run predates. Re-run the study to plot along a line.',
    fidelity: {
      exact: 'Exact',
      interpolated: 'Interpolated',
      recorded: 'Recorded',
    },
    fidelityExactTooltip:
      "Read off the solver's own cells — these values add no sampling error of their own.",
    fidelityInterpolatedTooltip:
      'Read off the uniform grid resampled for the 3-D viewer, not the solver mesh. Evenly spaced, so it is coarsest exactly where the solver chose to be fine — expect a few percent of field range near a wall.',
    voxelSize:
      'Grid pitch {{size}} mm; features narrower than that are not on this curve.',
  },
  grid: {
    pageLabel: 'Overview dashboard',
  },
  sections: {
    lineProbe: 'Line Probe',
    gridConvergence: 'Grid Convergence Index',
    paretoFrontier: 'Pareto Frontier',
    summary: 'Summary',
    methodology: 'Setup',
    results: 'Results',
    guards: 'Guards',
    surrogateValidation: 'Surrogate Validation',
    sensitivity: 'Sensitivity',
    writeup: 'Writeup',
    appendix: 'Appendix',
    chtResults: 'Conjugate Heat Transfer Results',
    chtHandCalcAnchors: 'Hand-calc anchors',
    thermalVisualization: 'Thermal Visualization',
    feaVisualization: 'FEA Visualization',
    flowVisualization: 'Flow Visualization',
    comments: 'Comments',
    commentsWithCount_zero: 'Comments',
    commentsWithCount_one: 'Comments ({{count}})',
    commentsWithCount_other: 'Comments ({{count}})',
  },
  dpSelector: {
    showing: 'Showing design point {{index}} of {{total}}',
    ariaLabel: 'Change design point',
    item: 'Point {{index}}',
  },
  dpChip: {
    attemptTagTitle: 'attempt {{attempt}} of {{total}}',
  },
  comments: {
    empty: 'No comments yet.',
    composerPlaceholder: 'Add a comment…',
    submitHint: 'Cmd/Ctrl + Enter to submit',
    submit: 'Comment',
    // BE cap on `CreateCommentRequest.body` (10001 chars). Surface the
    // same `{{current}} / {{max}}` counter shape as the report Writeup
    // editor so users don't hit a silent 422 on paste.
    charCount: '{{current}} / {{max}}',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    edited: '(edited)',
    unknownAuthor: 'Unknown',
    needsCommenter: 'You need commenter access or higher to comment.',
    signInToComment: 'Sign in to comment.',
  },
  pareto: {
    frontierSummary_one:
      'Frontier of {{count}} non-dominated design across {{runs}} completed run ({{dominated}} dominated). Dot size scales with hypervolume contribution; hover for design parameters. Hypervolume {{hypervolume}} as of iteration {{iteration}}.',
    frontierSummary_other:
      'Frontier of {{count}} non-dominated designs across {{runs}} completed runs ({{dominated}} dominated). Dot size scales with hypervolume contribution; hover for design parameters. Hypervolume {{hypervolume}} as of iteration {{iteration}}.',
    exportTitle: '{{studyName}} · Pareto frontier',
  },
  executive: {
    completion: 'Completion',
    wallClock: 'Wall-clock',
    paretoDesigns: 'Pareto designs',
    hvGain: 'HV gain',
    usable: 'Usable results',
    totalRuntime: 'Total runtime',
    medianPerPoint: 'Median per point',
    pick: 'Pick',
    design: 'Design',
    best: 'Best {{objective}}',
    paretoKnee: 'Pareto knee',
    sweptSummary_one: 'Swept {{count}} objective across {{runs}} completed run',
    sweptSummary_other:
      'Swept {{count}} objectives across {{runs}} completed runs',
    usingSampling: ' using {{method}} sampling',
    ranSummary_one: 'Completed {{count}} design-point run',
    ranSummary_other: 'Completed {{count}} design-point runs',
    paretoSentence_one:
      ' Pareto frontier contains {{count}} non-dominated design.',
    paretoSentence_other:
      ' Pareto frontier contains {{count}} non-dominated designs.',
    validationSentence: ' Surrogate validation: {{metrics}}.',
    driverSentence: ' Dominant driver: {{input}} on {{response}} (Sₜ={{st}}).',
  },
  methodology: {
    patchRegion: 'Surface areas from region',
    noConfig: 'No configuration recorded',
    inputs: 'Inputs',
    geometries: 'Geometries',
    meshes: 'Meshes',
    mesh: 'Mesh',
    strategy: 'Strategy',
    elementType: 'Element type',
    maxSize: 'Max size',
    minSize: 'Min size',
    elementOrder: 'Element order',
    cellCount: 'Cell count',
    cellsUnit: 'cells',
    skewnessMax: 'Skewness (max)',
    aspectRatioMax: 'Aspect ratio (max)',
    wedgeCellCount: 'BL prism cells',
    meshVolume: 'Mesh volume',
    perRegionVolumes: 'Per-region volumes',
    perInterfaceArea: 'Per-interface wetted area',
    boundaryLayer: 'Boundary layer',
    curvatureRefine: 'Curvature refine',
    externalEnclosure: 'External enclosure',
    on: 'on',
    sampling: 'Sampling',
    method: 'Method',
    n: 'N',
    scramble: 'Scramble',
    seed: 'Seed',
    solver: 'Solver',
    showAdvanced: 'Show advanced ({{count}})',
    hideAdvanced: 'Hide advanced',
    boundaryConditions: 'Boundary Conditions',
    objectivesOptimization: 'Objectives & Optimization',
    objectives: 'Objectives',
    maxIterations: 'Max iterations',
    batchSize: 'Batch size',
    // Row-label keys; `k` labels emitted by `buildSolverInfoRows` in
    // `-data/methodology-rows.ts`. The English strings live in the code
    // as the source-of-truth fallback; the i18n keys here drive the
    // translated rendering when a `TFunction` is passed. Keep the label
    // + fallback in sync (or drop the fallback when a locale bundle
    // reliably covers every deploy).
    rows: {
      template: 'Template',
      fluid: 'Fluid',
      density: 'Density',
      turbulence: 'Turbulence',
      flowAxis: 'Flow axis',
      inletVelocity: 'Inlet velocity',
      outletPressure: 'Outlet pressure',
      inletTemperature: 'Inlet temperature',
      exteriorThermal: 'Exterior thermal condition',
      adiabaticExterior: 'adiabatic (none declared)',
      targetTemperature: 'Target temperature',
      enclosureFaces: 'Enclosure faces',
      referenceArea: 'Reference area (A_ref)',
      referenceChord: 'Reference chord (l_ref)',
      hydraulicDiameter: 'Hydraulic diameter (D_h)',
      characteristicLength: 'Characteristic length (L)',
      bodySurfaceArea: 'Body surface area',
      inletArea: 'Inlet area',
      outletArea: 'Outlet area',
      enclosureArea: 'Enclosure surface area',
      projectedFrontalArea: 'Frontal area ({{axis}})',
      bodyBbox: 'Body bounding box (X × Y × Z)',
      enclosureBbox: 'Enclosure bounding box (X × Y × Z)',
      bodyVolume: 'Body volume',
      enclosureVolume: 'Enclosure volume',
      bcSets: 'BC sets',
      fixedTFaces: 'Fixed-T faces',
      endTime: 'End time',
      timestep: 'Timestep',
      timeAveragingStart: 'Averaging from',
      maxCourant: 'Max Courant',
      outerCorrectors: 'Outer correctors',
      writeInterval: 'Write interval',
      conductivity: 'Conductivity',
      heatFluxFaces: 'Heat-flux faces',
      radiationFaces: 'Radiation faces',
      convectionFaces: 'Convection faces',
      // Per-face / per-node thermal BC detail lines on Overview / Methodology.
      fixedTDetail: 'Fixed T · {{value}} K',
      heatFluxDetail: 'Heat flux · {{value}} W/m²',
      convectionDetail: 'Convection · h {{h}} W/(m²·K) · ambient {{ambient}} K',
      radiationDetail: 'Radiation · ε {{emissivity}} · ambient {{ambient}} K',
      pointHeatDetail: 'Point heat load · {{value}} W',
      nodeLabel: 'Node {{nodeId}}',
      volumetricHeatBc: 'Volumetric heat (BC)',
      surfaceHeatFlux: 'Surface heat flux',
      regions: 'Regions',
      fluidMaterial: 'Fluid material',
      fluidDensity: 'Fluid ρ (density)',
      fluidViscosity: 'Fluid ν (kinematic viscosity)',
      fluidCp: 'Fluid c_p (specific heat)',
      fluidConductivity: 'Fluid k (thermal conductivity)',
      fluidPrandtl: 'Fluid Pr (Prandtl)',
      solidMaterial: 'Solid material',
      solidProperties: 'Solid properties (k, ρ, c_p)',
      solidK: 'Solid k (thermal conductivity)',
      solidDensity: 'Solid ρ (density)',
      solidCp: 'Solid c_p (specific heat)',
      regionHeatSource: 'Region heat source',
      material: 'Material',
      yieldUltimate: 'Yield / Ultimate',
      volumeMaterials: 'Volume materials',
      restraint: 'Restraint',
      gravity: 'Gravity',
      centrifugal: 'Centrifugal',
      axialCoupling: 'Axial coupling',
      contact: 'Contact',
      boltPreload: 'Bolt preload',
      shrinkPreload: 'Shrink preload',
      modes: 'Modes',
      faces: 'faces',
      cutbacks: 'Increment cutbacks',
      cutbacksOn: 'Enabled',
      cutbacksOff: 'Disabled (fail fast)',
      frictionless: 'frictionless',
      derived: 'derived',
      studyDefault: 'study default',
      bonded: 'bonded',
      tieTolerance: 'tolerance {{mm}} mm',
      tieToleranceDefault: '2 mm (default)',
      cutbacksLimited: 'Enabled, at most {{n}} per increment',
      increments: 'Load increments',
      incrementsFirstMax: 'first {{initial}} of the load · max {{maximum}}',
      incrementsMin: 'min {{minimum}}',
      target: 'target',
      contactPairs: 'Contact',
      boltPreloads: 'Preload',
    },
    // Solver template labels rendered in the Template row (short forms
    //; the full template documentation lives in the SimType picker on
    // the draft flow).
    templates: {
      external_flow: 'External flow (CFD)',
      internal_flow: 'Internal flow (CFD)',
      urans: 'Transient flow (CFD)',
      cht_steady: 'Conjugate heat transfer (CHT)',
      thermal_conduction: 'Heat conduction (FEA)',
      thermal_transient: 'Transient heat (FEA)',
      structural_static: 'Structural static (FEA)',
      modal_frequency: 'Modal frequency (FEA)',
      linear_buckling: 'Linear buckling (FEA)',
    },
    // The same templates by short name, for a chip that names what a study
    // simulates (the design-point detail header, ENG-2935). The three
    // structural templates share one.
    templatesShort: {
      external_flow: 'External flow',
      internal_flow: 'Internal flow',
      urans: 'Transient flow',
      cht_steady: 'Conjugate heat transfer',
      thermal_conduction: 'Heat conduction',
      thermal_transient: 'Transient heat',
      structural: 'Structural',
    },
    // CFD per-patch BC-type labels used inside the BC-sets summary value
    // (e.g. "9 no-slip wall (9 faces); 1 velocity inlet (-X); ..."). Kept
    // lowercase because they sit in-line after a count. Overlaps
    // vocabulary-wise with `simopsDraft.bc.*` but stays here because the
    // report renders these inside a composed string, not as picker rows.
    bcTypes: {
      velocity_inlet: 'velocity inlet',
      pressure_outlet: 'pressure outlet',
      wall_no_slip: 'no-slip wall',
      slip: 'slip',
      symmetry: 'symmetry',
      freestream: 'freestream',
      fixed: 'Fixed',
      load: 'Load',
      pointLoad: 'Point load',
      pressure: 'Pressure',
      restraint: 'Restraint',
      prescribed: 'Prescribed',
      held: 'held',
      free: 'Free',
    },
    // Enclosure-face role words used inside the auto-derived enclosure
    // summary ("1 inlet (-Z) · 1 outlet (+Z) · 4 farfield"). Same three
    // roles the BE auto-classifies from `flow_axis` on external_flow.
    enclosureRoles: {
      inlet: 'inlet',
      outlet: 'outlet',
      farfield: 'farfield',
    },
    autoFromFlowAxis: 'auto from flow axis',
    // CHT region-count summary role words ("1 fluid · 2 solid").
    regionRoles: {
      fluid: 'fluid',
      solid: 'solid',
    },
    // Trailing "(custom override)" tag on the Fluid material row when the
    // region carries explicit rho/nu/cp/kappa overrides on top of the library
    // preset. Also used as the material display when the library key
    // resolves to `'custom'`.
    materialCustomOverride: 'custom override',
    // Hover-tooltip descriptions for the Methodology KV rows. External /
    // internal-flow variants of the inlet + outlet plane notes carry an
    // optional {{ax}} placeholder that the `planeNote` helper substitutes
    // via a plain `.replace(/\{\{ax\}\}/g, ...)` so the axis letter stays
    // in the same syntax slot across locales.
    units: {
      kinematicPressure: 'm²/s² (kinematic)',
    },
    titles: {
      inletPlaneExt:
        'Freestream velocity points along the flow axis, entering through the inlet plane normal to it.',
      inletPlaneExtWithAxis:
        'Freestream velocity points along the flow axis in the +{{ax}} direction, entering through the inlet plane (the −{{ax}} face) normal to it.',
      outletPlaneExt:
        'Static pressure on the outlet plane, normal to the flow axis.',
      outletPlaneExtWithAxis:
        'Static pressure on the outlet plane (the +{{ax}} face), normal to the flow axis.',
      inletPlaneInt:
        'Velocity points into the domain, normal to the inlet patch face.',
      outletPlaneInt:
        'Pressure applied on the outlet patch face, normal to it.',
      valueFromPatch: ' Value read from the {{patchType}} patch BC.',
      chtInletVelocity:
        'Fluid velocity entering the domain, directed normal to the inlet patch face.',
      adiabaticExterior:
        'No face declares a temperature, a film coefficient or a flux, so every surface that is not a region interface holds heat in. With no exterior path, steady state requires the interfaces to carry exactly the declared power.',
      declaredExterior:
        'Tagged faces carrying a thermal condition. Heat can leave without passing through the coolant, so the interface total need not equal the declared power.',
      chtInletTemperature: 'Temperature of the fluid entering at the inlet.',
      chtOutletPressure:
        'Static pressure at the outlet patch, normal to its face. Stored as kinematic pressure (p/ρ).',
      targetTemperature:
        'Design target temperature the study optimises toward.',
      chtFixedTFaces:
        'Solid faces held at a fixed temperature during the solve. Each face acts as a Dirichlet boundary — cells adjacent to it are pinned to the listed value, and heat flows in or out of the solid to close the balance.',
      uransEndTime:
        'Total physical time simulated. A transient run terminates on simulated time, not on an iteration count.',
      uransTimestep:
        'Starting physical timestep. adjustTimeStep narrows it live to hold the Courant target, so the run rarely keeps this value throughout.',
      uransTimeAveraging:
        'Mean fields are averaged over the window from this time to the end time, and that mean is the reported URANS result. Start it after the startup transient or the average carries it.',
      uransMaxCourant:
        'Courant target the adaptive timestep holds. pimpleFoam stays stable well above 1, but the answer drifts — lower it when spatial gradients drive the physics.',
      uransOuterCorrectors:
        'PIMPLE outer-loop count. 1 is PISO-only and drifts on non-orthogonal meshes; 2 is the canonical hybrid; higher approaches a full SIMPLE iteration per timestep.',
      uransWriteInterval:
        'Simulated time between field snapshots written to disk. It sets how finely the run can be replayed, not how it is solved.',
      thermalConductivity:
        'Thermal conductivity from the thermal material library. At steady state this is the only material property that reaches the answer — density and specific heat govern how fast a body gets there, not where it settles.',
      thermalFixedTFaces:
        'Faces held at a fixed temperature. Each is a Dirichlet boundary — heat flows in or out through it as needed to hold the value.',
      thermalHeatFluxFaces:
        'Faces carrying a prescribed surface heat flux. Positive flows heat INTO the body.',
      thermalConvectionFaces:
        'Faces coupled to an ambient bath through a film coefficient h, with q = h · (T_wall − T_ambient). Typical h: natural-convection air ~5, forced air ~50, water ~1000+.',
      thermalRadiationFaces:
        'Faces radiating to an ambient sink. The deck emits ε·σ, so the reported emissivity is what was authored, not the product.',
      chtConvectionFaces:
        'Solid faces coupled to an ambient bath through a heat-transfer coefficient h. Heat flux across the face reads q = h · (T_wall − T_ambient); higher h means the face sheds heat more aggressively. Typical h: natural-convection air ~5, forced air ~50, water ~1000+, boiling ~10k+.',
      chtVolumetricHeatBc:
        'Body-level heat generation applied to a solid region — watts per cubic metre distributed uniformly through the volume. The total power the solver injects is this density multiplied by the meshed region volume.',
      chtSurfaceHeatFlux:
        'Solid faces driven by a prescribed heat flux — watts per square metre normal to the face. Positive means heat flows into the solid; negative means heat is being extracted.',
      chtRegions:
        'CHT partitions the meshed volume into fluid and solid regions, each with its own material properties + BC set. Interfaces between them are coupled automatically so the temperature and heat flux match on both sides.',
      chtFluidMaterial:
        'Coolant fed to each fluid region — ρ, ν, c_p, and Pr come from the fluid material library keyed by this name. A "custom override" tag means at least one of the four was typed in by hand for this run.',
      chtSolidMaterial:
        'Solid material for each region — thermal conductivity k, density ρ, and specific heat c_p come from the thermal-material library keyed by this name.',
      chtFluidProperties:
        'ρ / ν / c_p / Pr for each fluid region. Library values unless the region carried a custom override (rho / nu / cp / pr fields).',
      chtFluidDensity:
        'Fluid density ρ per material (library value; custom override called out per region).',
      chtFluidViscosity: 'Fluid kinematic viscosity ν per material.',
      chtFluidCp: 'Fluid specific heat c_p per material.',
      chtFluidConductivity: 'Fluid thermal conductivity k per material.',
      chtFluidPrandtl:
        'Fluid Prandtl number Pr per material (dimensionless) — derived as ρ·ν·c_p/k, which is what the solver is handed.',
      chtSolidProperties:
        'k / ρ / c_p for each solid region. Values come from the thermal-material library keyed by the region material.',
      chtSolidK: 'Solid thermal conductivity k per material.',
      chtSolidDensity: 'Solid density ρ per material.',
      chtSolidCp: 'Solid specific heat c_p per material.',
      chtRegionHeatSource:
        'Heat source applied to each solid region. Unit is W for a total-power source (spread over the meshed region volume by the case writer) or W/m³ for a volumetric density (applied directly to every cell).',
      density:
        'Fluid density ρ. Also the factor that converts kinematic pressure (m²/s²) to absolute Pa.',
      outletKinematicWithRho:
        ' Stored as kinematic pressure (p/ρ); switch units to read absolute pressure (ρ = {{rho}} kg/m³).',
      outletKinematic: ' Stored as kinematic pressure (p/ρ).',
      structuralYieldUltimate:
        'Material yield and ultimate tensile strength, from the structural material library.',
      structuralVolumeMaterials:
        'Per-volume material overrides for multi-body assemblies.',
      structuralContactPair:
        'Surfaces that press but are not bonded: compression only, free to separate. Makes the solve nonlinear.',
      structuralShrinkPreload:
        'Preload produced by cooling the bolt. The force is a TARGET — a compliant joint delivers less, so the deck calibrates and the achieved force is reported with the results.',
      structuralBoltPreload:
        'Tension locked into the bolt through a pre-tension section cutting the shank.',
      structuralCutbacks:
        'Whether the solver may halve an increment that fails to converge. Disabled by default: a solve that cannot take its step stops instead of grinding down to a tiny one.',
      structuralTiedInterface:
        'Surfaces bonded together: they carry tension, shear and compression and never separate. Each dependent node within the tolerance is tied to the other face where it lies, bridging any gap between the two meshes. The solve stays linear.',
      thermalTiedInterface:
        'Surfaces bonded for temperature continuity: CalculiX *TIE equates temperature across the interface so heat conducts between independently meshed bodies. Nodes within the tolerance are tied; the solve stays linear.',
      structuralIncrements:
        'How the contact step applies its load: the first increment, the largest increment the solver may take, and the smallest it may cut back to, each as a share of the full load.',
      referenceArea:
        'Reference area used to normalize the lift and drag coefficients (C_L, C_D). Larger area → smaller coefficient for the same force.',
      referenceChord:
        'Reference length used to normalize the pitching-moment coefficient (C_M). Longer chord → smaller C_M for the same moment.',
      chtHydraulicDiameter:
        'Reader-declared D_h of the coolant channel. Feeds the hand-calc anchor D_h row + the Bi / Nu / j radar axes. Not consumed by the solver.',
      chtCharacteristicLength:
        'Reader-declared L for the hand-calc anchor set + the radar axes that scale with L. Not consumed by the solver.',
    },
    // "N face groups" header composing the BC-sets value. Uses the
    // i18next pluralization system so JA/zh-TW can drop the singular /
    // plural split (they don't inflect on count).
    faceGroupHeader_one: '{{count}} face group',
    faceGroupHeader_other: '{{count}} face groups',
    // "N faces" abbreviated fallback when the patch-name list would be
    // too long to display in the report cell.
    nFaces_one: '{{count}} face',
    nFaces_other: '{{count}} faces',
  },
  results: {
    runStatistics: 'Run statistics',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    inProgress: 'In progress',
    runtimePerPoint:
      'Runtime per completed point; median {{median}} · p95 {{p95}}',
    // Prefix for the failure-group list under §4.1; the trailing text is
    // the BE-emitted `error_message` (English today; BE ask to i18n).
    failureCount_one: '{{count}}×',
    failureCount_other: '{{count}}×',
    topDesigns: 'Top designs',
    dpTableHeading: 'Design-point table',
    deltaTableHeading: 'Design-point delta (each DP vs baseline DP)',
    by: 'By {{label}}',
    rank: 'Rank',
    design: 'Design',
    crossReferences:
      'Pareto frontier → Surrogate validation → Sensitivity → Grid convergence',
  },
  // ENG-1545 Block E-3; reference comparison. Anchor picker is
  // FE-only until BE ships a study-level `reference_id` field; the
  // feOnlyNote surfaces that so the reader knows a picked anchor
  // resets on reload.
  referenceComparison: {
    heading: 'Reference comparison',
    pickerLabel: 'Reference:',
    pickerPlaceholder: 'Pick a reference…',
    clear: 'clear',
    empty:
      'This is where a cited reference comparison lands. Pick a reference from the library above; a match rate outside its declared tolerance is the finding that turns a run into a validation.',
    noCompletedDps:
      'No completed design points yet; the picked reference has nothing to compare against.',
    noAnchorsForTemplate:
      "No published references in the library for this study's template. Skip this section or file a library addition.",
    dp: 'DP',
    quantity: 'Quantity',
    ours: 'Sim',
    reference: 'Reference',
    tolerance: 'Tolerance',
    source: 'Source',
    rePerDp: 'Re per DP:',
    chartCaption:
      'Solid: solver value per DP · dashed: reference correlation per DP · X = {{xAxis}}',
    bestMatchTooltip:
      'Smallest mean |Δ| against your run of the references in this library. Not a rank; confirm the shape matches before picking.',
    sensitivityMismatch:
      'This reference varies with {{anchorAxis}}, but your DOE sweeps {{sweepAxis}}; the reference curve will be flat across the run. Sweep {{anchorAxis}} (or pick a reference that depends on {{sweepAxis}}) to see it move.',
    feOnlyNote:
      'Under development. The picked reference is not saved yet; reloading the report resets the picker.',
    anchors: {
      sphereCd: {
        label: 'Sphere · C_D · Schiller–Naumann',
        citation:
          'Schiller & Naumann (1933). Über die grundlegenden Berechnungen bei der Schwerkraftaufbereitung. VDI, 77. Valid for a rigid smooth sphere in the subcritical Re regime (Re ≲ 3.5×10⁵); C_D drops sharply beyond this and the correlation no longer covers the miss.',
        qtyCd: 'C_D (drag coefficient)',
      },
      thinAirfoilCl: {
        label: 'Thin airfoil · C_L(α) · 2π theory',
        citation:
          "Anderson, J. D. Fundamentals of Aerodynamics, 6th ed. Thin-airfoil theory: C_L = 2π·α for a symmetric thin airfoil in inviscid incompressible flow. Reads α from each DP's angle of attack; produces the reference line on a Cl-vs-α sweep. ±10% covers typical NACA 24xx-range camber contributions; heavily cambered / high-lift sections sit outside the band.",
        qtyCl: 'C_L (lift coefficient)',
      },
      handCalcShapes: {
        citation:
          'Sandbox Hand-Calc shape library (Hoerner, Fluid-Dynamic Drag; Anderson, Fundamentals of Aerodynamics; ESDU aerodynamic data; White, Fluid Mechanics). Textbook mid-range values; sanity check, not publication-grade validation targets.',
      },
      suddenExpansion: {
        label: 'Pipe expansion · ζ · Borda-Carnot',
        citation:
          'Borda-Carnot loss for an abrupt area change: ζ = (1 − A_in / A_out)². White, Fluid Mechanics, 8th ed., §6.9. Valid for turbulent flow through a square-edged step; angled diffusers and non-round ducts sit outside the ±15% band.',
        qtyZeta: 'ζ (loss coefficient)',
      },
      pipeFrictionLaminar: {
        label: 'Pipe friction · ζ · Poiseuille (laminar)',
        citation:
          'Darcy-Weisbach with the Hagen-Poiseuille friction factor: f = 64 / Re, ζ = f · L / D_h. White, Fluid Mechanics, §6.4. Valid for a straight round duct at Re < 2300; transition + turbulent regimes need Colebrook / Blasius (follow-up anchor).',
        qtyZeta: 'ζ (loss coefficient)',
      },
    },
    shapeWarning: {
      generic:
        'Assumes the body matches: {{anchor}}. Confirm your geometry actually matches before quoting the deviation; the shape is never in the record, and picking the wrong reference does not read as an error, just as a bad comparison.',
    },
  },
  // ENG-1545 Figure 3-1; domain + BC schematic under Methodology.
  domainSchematic: {
    ariaLabel:
      'Domain schematic; rectangular enclosure with the body at centre, inlet arrows on the left, outlet callout on the right, farfield labels on top and bottom.',
    figureLabel: 'Domain schematic',
    sectionHeading: 'Boundary Conditions',
    caption: 'Domain and boundary conditions as meshed.',
    inletVelocity: 'velocity inlet',
    inletVelocityInternal: 'velocity inlet',
    uNotDeclared: 'U not declared',
    outletPressure: 'pressure outlet',
    pNotDeclared: 'p not declared',
    farfieldTop: 'farfield (slip-wall)',
    farfieldBottom: 'farfield (slip-wall)',
    wallLabel: 'wall (no-slip) · chord {{chord}}',
    wallLabelInternal: 'wall (no-slip)',
    nonWallPatchInternal: 'not every patch on this face is a wall',
    arefDeclared:
      'A_ref declared {{value}} m². Implied frontal area (chord × span) becomes available once the mesh bbox × span is reported; a >100× mismatch is the unit-scale check the Verification section will fire on then.',
    schematicCaveat: 'Not to scale or geometry.',
  },
  // ENG-1545 §3.1; methodology assumptions block, template-partitioned.
  // Each entry names an assumption and states the direction it pushes
  // the reported quantity relative to the physical flow (up / down /;).
  assumptions: {
    heading: 'Assumptions',
    intro:
      'Every assumption the numbers rest on. ↑ means the assumption pushes the reported quantity up; ↓ means an ignored or lumped effect pushes it down. ○ means the direction depends on the load path or cannot be assigned; the omission may still matter.',
    introPrefix: 'Every assumption the numbers rest on.',
    introInflating:
      'Inflating (↑) = the model treats something as bigger than the physics does.',
    introIgnoring:
      'Ignoring / lumping (↓) = the omitted effect is known to push the reported quantity down.',
    introNeutral:
      '○ = the direction depends on the load path or cannot be assigned; the omission may still matter.',
    direction: {
      up: 'inflating; the model overstates this vs. the physics',
      down: 'an ignored or lumped effect pushes the reported quantity down',
      none: 'direction depends on the load path or cannot be assigned',
    },
    steadyRans: {
      title: 'Steady RANS, k-ω SST',
      body: 'Turbulence is modelled only; any unsteadiness in the real flow appears here as the scatter band on the monitored quantities.',
    },
    incompressible: {
      title: 'Incompressible',
      body: 'At U = {{u}} m/s (M ≈ {{mach}}) compressibility effects are far below the reported precision.',
      bodyNoU:
        'Compressibility effects are treated as far below the reported precision; confirm against the inlet velocity if the flow is faster than a wind-tunnel setup.',
    },
    fullyTurbulent: {
      title: 'Fully turbulent boundary layer; no transition model',
      body: 'At low Reynolds numbers the real layer is likely part-laminar; modelling it fully turbulent pushes friction drag up relative to the physical flow.',
    },
    fullyTurbulentInternal: {
      body: 'A part-laminar entry length is not modelled; the reported pressure drop over the developing region is likely higher than the physical value.',
    },
    smoothWall: {
      title: 'Hydraulically smooth surface',
      body: 'Roughness is not modelled; on a real surface it would push drag up, so the reported C_D is lower than the physical drag.',
    },
    smoothWallInternal: {
      body: 'Roughness is not modelled; on a real surface it would push the pressure drop up, so the reported Δp is lower than the physical value.',
    },
    farfieldBlockage: {
      title: 'Farfield is a slip-wall boundary',
      body: "Blockage (the body's frontal area as a fraction of the tunnel cross-section) is small at the declared farfield ratio but still confining, pushing local velocities and lift-curve slope up. Under development: the blockage-ratio row will surface the exact fraction in the Verification section.",
    },
    fluidProperties: {
      title: 'Fluid at declared ρ and ν',
      body: 'Every coefficient (C_D, C_L, C_M) is normalised by q = ½ρU², so a wrong ρ or U rescales every one of them proportionally. The report cannot tell whether the declared values are right; only that the reader picked them.',
      bodyInternal:
        'The reported pressure drop Δp scales linearly with ρ, and the loss coefficient ζ = 2·Δp / (ρU²) scales with both ρ and U. A wrong ρ or U rescales both proportionally. The report cannot tell whether the declared values are right; only that the reader picked them.',
    },
    shapeAnalyst: {
      title: 'Section shape as confirmed by the analyst',
      body: 'The hand-calculation references in the Hand-calc sandbox are selected from this confirmation; not from the study name.',
    },
    geometryTags: {
      title: 'Inlet / outlet tags as declared',
      body: 'The report treats the patch tagged "inlet" as the intended inlet; a mislabelled outlet would still satisfy mass balance while measuring the wrong port. The reader confirms the tag matches their intent.',
    },
    // ENG-1917 — CHT-specific assumption rows. Reviewer's 10-row spec
    // covers the conjugate-heat-transfer envelope. Rows 3, 4, 5 have
    // no CFD counterpart; rows 4 (perfect thermal contact, ↓) and 6
    // (adiabatic external walls, ↑) pull in opposite directions on
    // temperature, and the reader learns here that the two partially
    // cancel.
    feaStatic: {
      title: 'Static, small-displacement equilibrium',
      body: 'Loads are applied without inertia or time history, and stiffness is never updated from the deformed geometry. There is no backend small-displacement cutoff: the rigid-body diagnostic only rejects an impossible increment above 10,000× the model diagonal (or 1,000,000 mm when size is unavailable), which is a failure detector rather than a validity limit.',
    },
    feaEnergyEquilibrium: {
      title: 'Mechanical equilibrium only',
      body: 'The solve balances applied loads against reactions. It does not solve temperature or thermal-energy conservation, so no thermal-equilibrium or energy-closure verdict can be inferred.',
      bodyWithPreload:
        'The solve balances applied loads against reactions. Thermal-shrink preload uses a prescribed fictitious temperature change to create strain; it does not solve heat transfer, thermal equilibrium, or energy conservation.',
    },
    feaLinearElastic: {
      title: 'Linear elastic materials',
      body: 'Stress is proportional to strain using the material library values. Plasticity, damage, creep, and temperature-dependent properties are not modelled.',
    },
    feaMaterials: {
      title: 'Declared material assignment',
      bodyGlobal:
        'One material is applied to the model. Every body is interpreted with the same elastic and density properties.',
      bodyByVolume:
        '{{materials}} material(s) are assigned across {{volumes}} volume(s). Each volume keeps its own elastic, strength, and density properties.',
    },
    feaSupports: {
      title: 'Declared supports define rigid-body restraint',
      body: '{{count}} fixed or axis-selective support(s) are applied exactly on their tagged faces or bodies. Compliance in the fixture itself is not included.',
    },
    feaLoads: {
      title: 'Declared load set only',
      bodyWithBodyForces:
        '{{loads}} surface, edge, pressure, or coupling load(s) and {{bodyForces}} gravity or centrifugal body load(s) are included.',
      bodyWithoutBodyForces:
        '{{loads}} surface, edge, pressure, or coupling load(s) are included. No gravity or centrifugal body load is declared, so self-weight and rotation are absent.',
    },
    feaInitialState: {
      title: 'Initial stress state',
      bodyNoPreload:
        'The assembly starts unstressed. No bolt preload or shrink-fit preload stage is declared.',
      bodyWithPreload:
        '{{count}} bolt or shrink-fit preload stage(s) establish the initial stressed state before the service loads are applied.',
    },
    feaContact: {
      title: 'Small-sliding contact',
      body: '{{count}} declared contact pair(s) transfer normal and tangential traction with small-sliding kinematics. The backend reports each pair’s slip scale as μ × peak contact pressure ÷ stick slope; motion below that scale can be elastic sticking, while finite sliding and large rotation are not modelled.',
    },
    feaCutbacks: {
      title: 'Nonlinear increment cutbacks',
      bodyEnabled:
        'Automatic cutbacks are enabled for contact or preload steps, allowing the solver to reduce an increment when equilibrium is difficult.',
      bodyDisabled:
        'Automatic cutbacks are disabled. A contact or preload step that cannot reach equilibrium at the scheduled increment fails instead of silently taking smaller steps.',
    },
    feaModalEigenvalue: {
      title: 'Linear, undamped eigenvalue analysis',
      body: 'The first {{count}} modes are extracted about the unstressed configuration. Applied loads, damping, contact opening, and preload stiffening are not part of this modal model.',
    },
    thermalConduction: {
      steadyState: {
        title: 'Steady-state conduction',
        body: 'The solve finds a time-independent temperature field. Startup transients and duty cycling are not modelled.',
      },
      singleMaterial: {
        title: 'Single solid material',
        body: 'Every volume uses {{material}} from the thermal material library.',
      },
      declaredBcs: {
        title: 'Declared face conditions',
        bodyNone:
          'No face carries an explicit thermal BC. Every untreated surface is adiabatic.',
        bodyWithInventory:
          'Tagged faces declare: {{inventory}}. Every other surface is adiabatic.',
      },
      inventory: {
        fixedT: '{{count}} fixed-T',
        heatFlux: '{{count}} heat flux',
        pointHeat: '{{count}} point heat load',
        convection: '{{count}} convection',
        radiation: '{{count}} radiation',
        separator: ', ',
      },
      adiabaticUntreated: {
        title: 'Untreated faces are adiabatic',
        body: 'Faces without a declared condition hold zero normal heat flux, so heat cannot leave except through the faces you named.',
      },
      noThermalInterfaces: {
        title: 'No thermal interfaces between bodies',
        body: 'Multi-body assemblies are not coupled across shared interfaces in this template. Each body is solved as a single material domain.',
      },
      perfectContactSharedNodes: {
        title: 'Perfect thermal contact at shared mesh joins',
        body: 'Bodies share nodes on contacting faces, so temperature is continuous and heat crosses with zero contact resistance — the same idealisation as a CHT coupled interface. Real joints and interface layers add resistance, so a physical assembly runs hotter at the same watts.',
      },
      perfectContactTies: {
        title: 'Perfect thermal contact at bonded ties',
        body: 'Each bonded tie equates temperature DOF across the interface (CalculiX *TIE), so heat conducts with zero contact resistance. Real joints add tenths of K/W; the physical assembly runs hotter than this model at the same watts.',
      },
      steadyMaterialProps: {
        title: 'Density and specific heat do not enter the steady answer',
        body: 'At steady state only conductivity affects the reported temperature field. ρ and c_p govern transient warm-up, not the settled field.',
      },
    },
    chtSteadyEquilibrium: {
      title: 'Steady equilibrium',
      body: 'Startup, pump ramps, and duty cycling appear only as convergence behaviour; the answer is the settled temperature field and says nothing about time-to-reach-it.',
    },
    chtFrozenFluidProperties: {
      title: 'Fluid properties held at one temperature',
      body: 'ρ, μ, c_p and k are the library values at the declared condition. Viscosity moves with temperature, so a run with a large fluid rise is solved with thicker fluid than it ends with, reading Δp high. Confirm when ΔT exceeds a few K.',
      bodyWithMaterial:
        'ρ, μ, c_p and k for {{fluid}} are the library values at the declared condition. Viscosity moves with temperature, so a run with a large fluid rise is solved with thicker fluid than it ends with, reading Δp high. Confirm when ΔT exceeds a few K.',
      bodyWithMaterialAndDT:
        'ρ, μ, c_p and k for {{fluid}} are the library values at the declared condition. This run posted a fluid rise of {{dt}} K — viscosity moves with temperature, so the fluid is solved thicker than it ends up, reading Δp high. Watch when ΔT exceeds a few K.',
    },
    chtNoBuoyancy: {
      title: 'Buoyancy absent',
      body: 'Density is fixed, so natural convection contributes zero heat transport whatever gravity is set to. Valid while Gr / Re² ≪ 1; a low-flow or no-flow scenario loses its only remaining heat path in this model.',
    },
    chtPerfectThermalContact: {
      title: 'Perfect thermal contact at every joint',
      body: 'The coupled-temperature interface carries zero contact resistance; interface layers and mechanical joints add tenths of K / W in practice, so the physical assembly runs hotter than this model at the same watts. Usually the largest single gap between this model and a physical measurement.',
    },
    chtUniformHeatSource: {
      title: 'Heat spread uniformly through the source volume',
      body: 'The declared watts fill the whole source region evenly; localised hotspots inside the source sit above the reported region maximum temperature.',
      bodyWithQ:
        'The declared {{q}} fills the whole source region evenly; localised hotspots inside the source sit above the reported region maximum temperature.',
    },
    chtAdiabaticExternalWalls: {
      title: 'External walls adiabatic',
      body: 'Every watt is forced through the fluid with zero free cooling from the outer surfaces, so reported temperatures sit above a physical assembly that also loses heat to ambient. Conservative for thermal margin.',
    },
    chtNoRadiation: {
      title: 'Radiation absent',
      body: 'A few percent of the transfer at these temperatures, growing with T⁴; revisit for passive or air-cooled configurations.',
    },
    chtTurbulenceModel: {
      title: 'Turbulence modelled (k-ω SST), heat bridged by constant Pr_t',
      body: 'h inherits the wall treatment quality: the wall-spacing check governs how much to trust the convective side. Laminar runs replace this row with "confirm Re is inside the laminar range".',
      bodyWithModel:
        'Turbulence modelled ({{model}}), heat bridged by constant Pr_t. h inherits the wall treatment quality: the wall-spacing check governs how much to trust the convective side.',
      titleLaminar: 'Laminar flow assumed',
      bodyLaminar:
        'No turbulence closure; the momentum and thermal boundary layers are resolved directly. Confirm Re sits inside the laminar range for this passage — a transition-region case reads h low.',
    },
    chtUniformInlet: {
      title: 'Uniform inlet: one temperature, one velocity',
      body: 'Manifold maldistribution upstream of the port appears here as an inlet-condition error, tied to the development-length footnote.',
      bodyWithT:
        'Inlet is one temperature ({{t}} °C) and one velocity across the port. Manifold maldistribution upstream appears here as an inlet-condition error, tied to the development-length footnote.',
      bodyWithTAndU:
        'Inlet is one temperature ({{t}} °C) and one velocity ({{u}} m/s) across the port. Manifold maldistribution upstream appears here as an inlet-condition error, tied to the development-length footnote.',
    },
    chtAdiabaticExterior: {
      title: 'Exterior faces are adiabatic',
      body: 'No thermal condition is declared on any face, so every surface that is not a region interface holds heat in. A physical assembly also sheds heat to the room, so solid temperatures read HIGH — and with no exterior path, steady state requires the interfaces to carry exactly the declared power.',
    },
    chtDeclaredExterior: {
      title: 'Exterior faces carry declared conditions',
      body: '{{count}} tagged face(s) name a temperature, a film coefficient or a flux. Heat can leave the assembly without passing through the coolant, so the interface total is not expected to equal the declared power.',
    },
    chtSolverSafetyLimits: {
      title: 'Solver safety limits armed',
      body: 'Untouched limits are invisible; heavy clamping means the answer was held inside bounds by the solver. The clamping check grades how much clamping was actually applied.',
      bodyWithLimits:
        'Temperature clamped to [{{tMin}}, {{tMax}}] °C; velocity clamped at ~{{uMax}} m/s. Untouched limits are invisible; heavy clamping means the answer was held inside bounds by the solver.',
    },
  },
  // Overview headline for a completed CHT run. Names the hottest
  // solid when BE ships `hottest_solid_region` (ENG-1927); falls back
  // to "solid" so a heat-exchanger or battery module reads as
  // "solid" rather than "chip".
  chtHeadline: {
    peakNamed: 'Peak temperature in {{region}}: {{tmax}}.',
    peakUnnamed: 'Peak solid temperature {{tmax}}.',
    withinNamed:
      'Peak temperature in {{region}}: {{tmax}} — within the {{limit}} limit.',
    withinUnnamed:
      'Peak solid temperature {{tmax}} — within the {{limit}} limit.',
    exceedsNamed:
      'Peak temperature in {{region}}: {{tmax}} — exceeds the {{limit}} limit.',
    exceedsUnnamed:
      'Peak solid temperature {{tmax}} — exceeds the {{limit}} limit.',
  },
  // ENG-1917 §3 — CHT region-graph schematic.
  regionGraph: {
    heading: 'Region graph',
    intro:
      'Every heat path this run models is drawn. Q̇ enters the source solid (left). Grey arrows are solid-to-solid conduction. Blue arrows are convection into the coolant. Every arrow label is the NET TOTAL watts crossing that interface at steady state — the run-integrated rate, not a peak or an average. Signs are absolute here; the direction is what the arrow tells you. Peak flux (W/m²) sits on the overview cards, not on the arrows.',
    parallelFlowNote:
      'Solid boxes are laid out left to right by their strongest neighbour edge. When a solid interfaces MORE than one neighbour, only the primary chain edge is drawn; the extra edges still contribute to the totals inside the boxes. The full breakdown per interface is available on the DP-detail results row.',
    stateToggleAria: 'Show declared inputs or the solved outputs',
    stateDeclared: 'Declared',
    stateSolved: 'Solved',
    stateDeclaredHint:
      'Show what the setup asked for — inlet temperature, Q̇, target T. The temperature inside each box reads the BC.',
    stateSolvedHint:
      'Show what the solve delivered — steady-state region T, interface fluxes, coolant ΔT.',
    ambientLabel: 'ambient · adiabatic (no external heat flow)',
    ambientLabelDeclared: 'ambient · exterior conditions declared',
    sourceMarker: 'source',
    fluidRangeMissing: 'no temperature range on the summary',
    hottest: 'Hottest solid on this run.',
    hottestBadge: 'hot',
    regionRoleSolid: 'Solid',
    regionRoleFluid: 'Fluid',
    tooltipHeatSource: 'Declared heat source',
    tooltipHeatSourceUnknown:
      'Declared heat source: total power not reported by this run.',
    tooltipConvection: 'Convection to coolant',
    tooltipConvectionUnknown:
      'Convection watts not reported on this run. This face is NOT adiabatic. The run has a real gradient between this solid and the coolant, so heat is crossing; the number is simply unrecorded here.',
    tooltipConduction: 'Conduction to next solid',
    tooltipConductionUnknown:
      'Conduction watts not reported on this run. This interface is NOT adiabatic. Adjacent solids share a face and have a temperature gradient across it, so heat is crossing; the number is simply unrecorded here.',
    tooltipSignHint:
      '(sign: + = heat leaving this solid into the coolant; − = coolant heating this solid)',
    tooltipCoolantIn: 'Inlet',
    tooltipCoolantOut: 'Outlet',
    tooltipCoolantDelta:
      'Coolant ΔT. Magnitude shown; sign convention varies by template.',
  },
  // ENG-1545 Block E-2; DP-vs-baseline delta table.
  deltaTable: {
    baselineLabel: 'Baseline:',
    dp: 'DP',
    needsMultipleDps:
      'Delta table needs at least two completed design points; a comparison against one DP is either zero or self.',
    caption_one:
      '{{count}} DP compared · cells show absolute Δ (percent Δ) against the baseline',
    caption_other:
      '{{count}} DPs compared · cells show absolute Δ (percent Δ) against the baseline',
  },
  // ENG-1545 Block E-1; compact DP table.
  dpTable: {
    dp: 'DP',
    verdict: 'Verdict',
    empty: 'No completed design points with a result summary yet.',
    caption_one:
      '{{count}} design point · click a header to sort · verdict from run status, convergence and mass balance',
    caption_other:
      '{{count}} design points · click a header to sort · verdict from run status, convergence and mass balance',
    verdicts: {
      pass: 'Passes',
      warn: 'Check',
      fail: 'Fails',
      stopped: 'Stopped',
      notEvaluated: 'Not evaluated',
    },
    verdictReasons: {
      stopped:
        'The run stopped after {{iters}} iterations before convergence was confirmed. The saved values are partial; this is not a divergence verdict.',
      pass: 'Solver converged; residual target met before the iteration budget ran out.',
      warn: 'Solver converged, but the mass imbalance is {{pct}} % against the {{bar}} % bar — {{factor}}× over. The domain is not fully closing.',
      // Three shapes for one verdict: the bar and the distance where the run
      // carries a residual target, the measured residual alone where it does
      // not (which is every run until BE emits `residual_targets`), and the
      // iteration count alone where no residual was reported either.
      fail: 'Solver did not converge. Worst residual {{eq}} = {{value}} against its {{target}} target — {{factor}}× over — after {{iters}} iterations.',
      failNoTarget:
        'Solver did not converge. Worst residual {{eq}} = {{value}} after {{iters}} iterations. This run carries no residual target, so how far past the bar it landed is not on the wire.',
      failSplitTarget:
        'Solver did not converge. Residual {{eq}} = {{value}} after {{iters}} iterations. The fluid and solid regions set different targets for it and the run reports one residual for both, so it is not compared with either.',
      failNoResiduals:
        'Solver did not converge and reported no per-equation residuals, so only the iteration count is known: {{iters}}.',
      notEvaluated:
        'Not enough signal to decide; the run did not emit convergence or mass-balance fields.',
      noSignals:
        'No result summary on this design point yet; the verdict fills in once the run completes.',
    },
  },
  // ENG-1545 Block A; verification panel (Section 4 "should I believe
  // this run"). Keys shared by the top-level panel and every per-DP card.
  verification: {
    // BE ships English check `evidence` / `reason` today. Pattern-match
    // known sentences (translateVerificationEvidence) until the wire
    // carries stable message keys.
    evidence: {
      wallBufferFails:
        '{{frac}}% of the wall area sits in the y⁺ 5–30 buffer band where neither wall treatment is valid (limit {{limit}}%) — friction numbers from this run are not admissible',
      wallBufferPassesCaveat:
        '{{frac}}% of the wall area is in the y⁺ 5–30 buffer band — acceptable, but any friction claim carries this caveat',
      wallBufferPasses:
        '{{frac}}% of the wall area is in the y⁺ 5–30 buffer band',
      flowTypeMismatch:
        '{{template}} template is running on {{mesh}} mesh — the wrong problem answered well is still the wrong problem',
      stagnationFails:
        'Peak wall pressure is {{ratio}}× dynamic pressure against an expected {{expected}} — the pressure field and the declared flow conditions disagree',
      stagnationPasses:
        'Peak wall pressure is {{ratio}}× dynamic pressure, within {{tol}}% of the expected {{expected}}',
      // thermal_transient / thermal_conduction verification evidence (ENG-3002).
      // Reader-facing copy stays plain — no *SECTION PRINT / RFL / ∫Q wire
      // jargon. The BE still ships the English wire sentence; translate-guard
      // matches that and interpolates these strings.
      energyImbalanceFailsWithFlows:
        'Energy imbalance {{imbalance}} J exceeds the {{tolerance}} J band. That band is about 1% of the largest energy term, widened when a share of faces ({{fraction}}) still use estimated recovered-gradient heat rates, plus a small floor of {{absTol}} W over {{period}} s. Face heat is integrated over time; estimated rates can widen the residual — that points at mesh resolution near those faces, not a broken conservation law. (Energy in {{energyIn}} J, stored change {{deltaE}} J)',
      energyImbalanceFails:
        'Energy imbalance {{imbalance}} J exceeds the {{tolerance}} J band. That band is about 1% of the largest energy term, widened when a share of faces ({{fraction}}) still use estimated recovered-gradient heat rates, plus a small floor of {{absTol}} W over {{period}} s. Face heat is integrated over time; estimated rates can widen the residual — that points at mesh resolution near those faces, not a broken conservation law.',
      energyImbalancePassesWithFlows:
        'Boundary heat and stored-energy change close within {{tolerance}} J. Face rates are integrated over time from direct measurements (reactions, applied flux, film temperature) when available; estimated recovered-gradient rates are only a last resort. (Energy in {{energyIn}} J, stored change {{deltaE}} J)',
      energyImbalancePasses:
        'Boundary heat and stored-energy change close within {{tolerance}} J. Face rates are integrated over time from direct measurements (reactions, applied flux, film temperature) when available; estimated recovered-gradient rates are only a last resort.',
      energyImbalanceUnavailable:
        'This transient run drew up no integrated energy balance, so its closure was never measured.',
      temperatureBoundsFails:
        'Temperatures left the [{{lo}}, {{hi}}] K band from initial conditions and boundary/ambient values (observed [{{obsMin}}, {{obsMax}}] K, tolerance {{tol}} K). Increase the first increment or refine the mesh near the stepped boundary.',
      temperatureBoundsPasses:
        'All temperatures stayed within [{{lo}}, {{hi}}] K (±{{tol}} K print noise).',
      boundaryFluxUnresolved:
        'The two boundary face integrals differ by {{imbalance}} W, more than the {{tolerance}} W band ({{heatIn}} W in, {{heatOut}} W out), so the mesh does not resolve the temperature gradient at one or more BC faces and a per-face heat rate read from the gradient there is unresolved. Re-mesh with a finer boundary layer at the BC faces before relying on those rates. The temperatures and the solve itself are unaffected — a steady conduction solve conserves energy by construction.',
    },
    cht: {
      coolantMassBalance: 'Coolant mass balance',
      coolantMassBalanceSub:
        '{{imbalance}} kg/s out of {{flow}} kg/s through the coolant region.',
      energyClosure: 'Energy imbalance',
      energySub: '{{in}} W in, {{out}} W removed.',
      heatIn: '{{value}} W in',
      heatOut: '{{value}} W out',
      // The parser's own reasons a failing balance may not be the run's
      // fault. Backend ships them as English prose; these are what a
      // translated session reads instead.
      explained: {
        notConverged:
          'convergence was not confirmed; this partial balance does not prove the solution diverged',
        limiterClamped:
          'a limiter clamped cells, which rewrites the field without rewriting the flux computed from it, and is already expected to read high',
      },
      energyExplainedBy: 'Interpret with caution: {{reasons}}.',
      temperatureTarget: 'Temperature target',
      targetSub: 'Declared limit {{limit}} K.',
      interfaceFlux: 'Heat into coolant',
      interfaceFluxSub: 'Peak {{peak}} W/m² on {{patch}}.',
      balance: {
        success: 'closes',
        warning: 'loose',
        destructive: 'does not close',
      },
      energy: {
        ok: 'closes',
        warn: 'loose',
        fail: 'does not close',
        provisional: 'partial',
      },
      target: { met: 'met', missed: 'missed', notRequested: 'none declared' },
      tooltips: {
        coolantMassBalance:
          'Coolant leaving minus coolant entering, as a fraction of throughput. The run reports an absolute kg/s rate; the fraction is shown so it reads against the same thresholds a CFD mass imbalance does. Above a few percent the solve is not conserving mass and no downstream number is trustworthy.',
        energyClosure:
          'This checks heat-transfer rates across the whole-model control volume, in watts (W), not stored energy. Imbalance = 100 × (heat in − heat out) / heat in. 0% means they match; +100% means no heat removal was reported. Internal solid–fluid transfer is not an external sink. An unfinished run may not balance yet.',
        energyProvisional:
          'This run stopped before a final solution. A missing outlet rate may reflect incomplete output; this percentage does not establish physical heat accumulation or numerical divergence.',
        heatIn:
          'Heat rate declared inside the model, including solid heat sources. This is power in W, not an amount of stored energy.',
        heatOut:
          'Heat rate reported leaving the model through external boundaries. Internal transfer between regions does not count. This is power in W, not stored energy.',
        temperatureTarget:
          'Whether the hottest solid stayed under the declared limit. Three states, not two: no limit declared is not a pass, and this row says so rather than showing a margin nothing checked.',
        interfaceFlux:
          'Net heat the solids give the coolant across every fluid-solid interface (negative: the coolant is heating the solids), and the patch carrying the highest flux density. The energy-closure anchor — a number, not a gate, since it is one side of the balance above.',
      },
    },
    heading: 'Verification',
    subhead:
      'Convergence, mass balance, turbulence and force-decomposition signals per design point.',
    noneAvailable:
      'No design point has finished running yet; verification signals appear once at least one run completes.',
    designPoint: 'DP {{id}}',
    notEvaluated: 'not evaluated',
    convergence: 'Convergence',
    converged: 'converged',
    // ENG-3042: converged means "did not diverge", which a run at its
    // iteration limit can be with its residuals still above target.
    convergedAtIterationLimit: 'converged (no divergence) at iteration limit',
    notConverged: 'not converged',
    // Empty {{max}} interpolation from the panel when BE reports no
    // iteration ceiling; collapses to "(2000 iters)" instead of the
    // ugly "(2000 /; iters)".
    itersLabel: '({{used}}{{max}} iters)',
    // ENG-2813: which of a steady run's three exits ended it.
    exitReasonLabel: 'Why it stopped',
    exitReason: {
      converged: 'Every equation met its target',
      iterationLimit: 'Reached its iteration limit',
      timeLimit: 'Ran out of time',
    },
    // ENG-2865: the same two limits when they stopped the run, which can
    // then be continued from where it stopped.
    stopLimit: {
      iterationLimit: 'Stopped: iteration limit reached',
      timeLimit: 'Stopped: time limit reached',
    },
    exitReasonTimeSub:
      'The time limit ended this run, not its residuals, so it does not point to a setup problem on its own.',
    // ENG-2812: a CHT equation whose fluid and solid targets differ.
    regionFluid: 'fluid',
    regionSolid: 'solid',
    regionTargetsNote:
      'The fluid and solid regions set different targets for {{equations}}. The run reports one residual for both regions, so it is not compared with either target.',
    hitIterLimit: 'hit iter limit',
    silentPassSuspect: 'targets met, yet ran to iteration limit',
    terminatingCriterion: 'Terminating criterion',
    cumulativeContinuity: 'Cumulative continuity',
    massImbalance: 'Mass imbalance',
    percentile: 'percentile',
    // ENG-2874: the table grades the initial residual, so the heading
    // must not say "final" above it.
    residualsHeading: 'Residuals',
    residualImbalanceRelation: {
      title: 'How this imbalance is computed',
      stepLabel: 'Step {{position}} of {{total}}',
      energyHeading: 'Energy imbalance (from boundary heat rates)',
      energyEquationAria:
        'epsilon_E equals 100 times (Q_in minus Q_out) over Q_in',
      energyPluggedAria:
        '{{pct}} percent equals 100 times ({{in}} minus {{out}}) over {{in}} watts',
      energyScopeNote:
        'Q_in and Q_out are whole-model external-boundary heat-rate totals.',
      varQinMeaning: 'heat entering at external boundaries',
      varQoutMeaning: 'heat leaving at external boundaries',
      varEpsilonEMeaning: 'energy imbalance from those totals',
      interfaceNotInFormula:
        'Heat into the coolant across the interfaces ({{value}} W) is listed separately. ε_E uses only Q_in and Q_out.',
      massHeading: 'Mass imbalance (from coolant throughput)',
      massHeadingFlow: 'Mass imbalance (from inlet and outlet flow)',
      massStepImbalanceAria: 'm_dot_imbalance equals m_dot_in plus m_dot_out',
      massStepImbalanceNote:
        'Signed mass-flow sums at the coolant inlet and outlet. Inward flow is negative and outward flow is positive; their sum is the net boundary mass imbalance.',
      massStepImbalanceNoteFlow:
        'Signed mass-flow sums at the inlets and outlets. Inward flow is negative and outward flow is positive; their sum is the net mass imbalance.',
      massStepRefAria:
        'm_dot_ref equals the larger of the absolute values of m_dot_in and m_dot_out',
      massStepRefNote:
        'The reference flow is the larger of the inlet and outlet magnitudes. If the outlet reports no flow at all, the imbalance is 100%, not undefined.',
      massEquationAria:
        'epsilon_m equals absolute value of m_dot_imbalance over m_dot_ref',
      massStepFracNote:
        'Relative imbalance: the net boundary flow as a share of the larger of the inlet and outlet flows. It comes from the boundary flows, not from a residual.',
      massPluggedAria:
        '{{pct}} percent equals absolute value of {{imbalance}} over {{flow}} kilograms per second',
      massPluggedFrac: '{{pct}} % of the larger flow',
      massScopeNote:
        'ṁ_imbalance and ṁ_ref come from the coolant inlet and outlet of the fluid region.',
      massScopeNoteFlow:
        'ṁ_imbalance and ṁ_ref come from the inlet and outlet openings of the flow domain.',
      varMInMeaning: 'coolant mass flow at the inlet (inward negative)',
      varMOutMeaning: 'coolant mass flow at the outlet (outward positive)',
      varMImbalanceMeaning: 'net coolant mass imbalance (ṁ_in + ṁ_out)',
      varMRefMeaning:
        'the larger of the coolant inlet and outlet mass flows (max(|ṁ_in|, |ṁ_out|))',
      varEpsilonMMeaning: 'mass imbalance as a fraction of the larger flow',
      linkHeading: 'Last-iteration residuals: a separate measure',
      linkNote:
        'Residuals measure how settled the solution is cell by cell. The imbalance above is computed from the flows at the boundaries, not from the residuals, so a near-zero imbalance beside a residual above its target is normal. Read the two separately.',
      askAssistantAgent: 'Agent',
      askAssistant: 'Ask how residual(h) and ε_E relate on this run',
      askAssistantLabel:
        'Ask the assistant how residual(h) and this energy imbalance relate on this run',
      askAssistantTitle: 'ε_E and residual(h)',
      askAssistantNoTarget: 'none recorded',
      askAssistantPrompt:
        'On this design point, residual(h) is {{residual}} (converge-exit target {{target}}). Heat in is {{qin}} W and heat out is {{qout}} W, so ε_E is {{pct}} %. The residual measures how settled the energy solution is; ε_E is computed from the boundary heat totals, not from the residual. How do the two relate on this run, and does either give a reason to doubt the result? Do not assume they must agree. Answer in plain language; do not name tools or wire field paths.\n\nContext for your tools only — do not repeat to me: study {{studyId}}, design point {{dpId}}.',
      linkEnergyLabel: 'Energy',
      linkMassLabel: 'Mass / continuity',
      linkResidualVsTarget:
        'residual {{residual}}, converge-exit target {{target}}',
      linkResidualNoTarget:
        'residual {{residual}}, no converge-exit target recorded',
    },
    relaxation: 'Relaxation',
    relaxationTooltip:
      'The submitted under-relaxation factor (0–1). Smaller values update the field more cautiously. A dash means this run did not report the setting.',
    residualTooltips: {
      U: 'Velocity equation.',
      p: 'Pressure equation.',
      p_rgh: 'Pressure corrected for gravity; helps enforce mass flow.',
      h: 'Energy equation for fluid or solid.',
      T: 'Temperature equation.',
      k: 'Turbulent kinetic energy equation.',
      omega: 'Turbulence specific dissipation equation.',
      epsilon: 'Turbulence dissipation equation.',
      other: 'Residual for the {{equation}} equation.',
    },
    equation: 'Equation',
    final: 'Final',
    // ENG-2874. The number the target actually grades: each equation's
    // residual at the START of the last step, before that step's solve.
    // "Initial" is the solver's own word for it and the one a user will
    // see in any OpenFOAM log, so it is kept rather than paraphrased.
    residual: 'Initial residual',
    target: 'Target',
    overTarget: 'above target',
    noResiduals: 'No residual data reported.',
    boundingHeading: 'Bounding events',
    field: 'Field',
    count: 'Count',
    firstIter: 'First iter',
    maxMagnitude: 'Max magnitude',
    forcesHeading: 'Forces; pressure / viscous split',
    forcesCaption:
      'Pressure = normal traction (form drag driver). Viscous = tangential traction (skin friction). Never sum these; a matching total can hide two large compensating errors.',
    axis: 'Axis',
    pressureN: 'Pressure ({{unit}})',
    viscousN: 'Viscous ({{unit}})',
    coefficientsHeading: 'Force coefficients',
    lOverD: 'L / D',
    copFracLref: 'CoP fraction (l_ref)',
    copSuppressed: 'suppressed (|C_L| < 1e-3)',
    refGeometry: 'A_ref = {{area}} · l_ref = {{chord}}',
    yPlusHeading: 'Wall y⁺',
    yPlusMin: 'min',
    yPlusMean: 'mean',
    yPlusMax: 'max',
    yPlusFracBelow5: 'frac y+ <5',
    yPlusFrac5To30: 'frac y+ 5–30',
    yPlusFrac30To100: 'frac y+ 30–100',
    yPlusFracAbove100: 'frac y+ >100',
    yPlusBandsPending:
      'Area-fraction bands (<5 / 5–30 / 30–100 / >100) are under development; min/mean/max alone can mask boundary-layer coverage.',
    externalDomainHeading: 'Domain & blockage',
    blockageRatio: 'Blockage ratio',
    domainExtentsX: 'Extent X (body diameters)',
    domainExtentsY: 'Extent Y (body diameters)',
    domainExtentsZ: 'Extent Z (body diameters)',
    externalDomainPending:
      'Blockage ratio + per-axis extents are under development; a rise from a familiar 0.55% to 2.18% currently surfaces nowhere.',
    freestreamHeading: 'Freestream turbulence',
    freestreamNutOverNu: 'ν_t / ν (freestream)',
    turbulenceIntensity: 'Turbulence intensity',
    turbulentLengthScale: 'Turbulent length scale',
    freestreamPending:
      'Derived ν_t / ν at the inlet is under development; users declare intensity + length scale but nobody computes the ratio, so a 5 500× tunnel-realistic 10 slips past every check.',
    // Per-row hover glossary. Kept short; the tooltip should give a
    // reader who does not know the term enough to decide whether to
    // trust the row, no more.
    tooltips: {
      convergence:
        'Whether the solver reached its residual control before running out of iterations. "Not converged" flags a run that ran out of budget; the numbers below are still what the solver produced, but you are looking at an unfinished field, not a settled one.',
      terminatingCriterion:
        'Reason the solver stopped iterating; residual control met, iteration budget exhausted, or a solver-side abort.',
      exitReason:
        'A steady run stops in one of three ways: every equation meets its target, it reaches its iteration limit, or it runs out of time. Only the first counts as converged.',
      cumulativeContinuity:
        'Total mass-flux imbalance accumulated across the run. A steady solve should decay toward zero; a monotonic rise means the pressure field is acting as a spurious mass source. The sparkline shows the trajectory so a flat / decaying curve is distinguishable from a growing one at a glance.',
      massImbalance:
        'Fraction of inlet flow that did not exit through the outlets (or reverse). Non-zero on external flow is expected (BC mismatch), but internal-flow values above ~1% usually indicate an unclosed circuit.',
      nutRatio:
        'Percentile of turbulent viscosity ratio (ν_t / ν) across the domain. If P50 is below 1 the turbulence model is barely contributing; a laminar solve labelled as SST. Range in the log-law region: ~10 to a few hundred.',
      cd: 'Non-dimensional drag coefficient; total streamwise force divided by ½ρV²A_ref. Sign convention: positive with the flow.',
      cl: 'Non-dimensional lift coefficient; total cross-stream force divided by ½ρV²A_ref.',
      cmPitch:
        'Pitching-moment coefficient about the reference point (usually the quarter-chord for airfoils). Sign convention follows the study configuration.',
      lOverD: 'Lift-to-drag ratio, C_L / C_D. Higher is more efficient.',
      copFracLref:
        'Centre-of-pressure position along l_ref, measured as C_M,pitch / C_L. Meaningless when |C_L| is near zero; the panel suppresses it below 1e-3 rather than surface a plausible-looking noise value.',
      yPlusMin:
        'Minimum wall y⁺ over the object surface. Values below 5 are in the viscous sublayer; wall functions do not hold there and shear stress / heat flux from log-law extrapolation are unreliable.',
      yPlusMean:
        'Area-weighted mean wall y⁺. A useful anchor, but obscures whether a large fraction of the wall is in the viscous sublayer or in the outer log-law region.',
      yPlusMax:
        'Maximum wall y⁺. Values above ~300 are outside the log-law valid range; the mesh is too coarse near the wall to resolve shear correctly.',
      pressureDrop:
        'Physical pressure drop across the domain, p_in − p_out (Pa). Positive on a driven-inlet channel; negative means a pressure RISE (sudden expansion, diffuser, or reversed BC).',
      pressureDropKinematic:
        'Kinematic pressure drop (m²/s²); what the solver writes internally, i.e. Δp / ρ. Multiplied by ρ to get physical Pa.',
      inletFlowRate:
        'Volumetric flow rate at the inlet (m³/s). BE convention: negative on the inlet (flow enters against the outward face normal), positive on the outlet; a matching |Q_in| = |Q_out| is a mass-balance check.',
      outletFlowRate:
        'Volumetric flow rate at the outlet (m³/s), positive on a driven-inlet channel. Compare against |Q_in| for a mass-balance check.',
      portVelocity:
        'Area-averaged velocity through the inlet / outlet patch (m/s). Divergence between the two on a constant-density solve indicates either a mass imbalance or a port-area mismatch.',
      wallPressureRange:
        'Wall-pressure extrema (Pa); a cheap sanity check for a spurious source or a BC-side glitch. Compare the range against ρU²/2 to gauge whether the wall is doing plausible work.',
      yPlusFracBelow5:
        'Fraction of wall area in the viscous sublayer (y+ < 5). Wall functions do not hold here; a large fraction means shear stress / heat flux extrapolated from the log-law is unreliable.',
      yPlusFrac5To30:
        'Fraction of wall area in the buffer layer (y+ 5–30). Neither the viscous sublayer nor the log-law dominates; results are unpredictable and this fraction should be small.',
      yPlusFrac30To100:
        'Fraction of wall area in the log-law region (y+ 30–100). This is the intended regime for wall-function turbulence models. High fraction = good.',
      yPlusFracAbove100:
        'Fraction of wall area above the log-law region (y+ > 100). Mesh is too coarse near the wall; shear stress is unreliable.',
      blockageRatio:
        'Frontal area of the body divided by the enclosure cross-section at the same station. Above ~5% the confinement changes the flow around the body; a benchmark comparison against a free-stream reference stops being fair.',
      domainExtents:
        'Domain extent from the body to the outer boundary, expressed in body diameters. A "far enough" reference the reader would otherwise derive from the enclosure config plus the body bounding box.',
      freestreamNutOverNu:
        'Derived turbulent viscosity ratio at the inlet, computed from the declared turbulence intensity and length scale. Tunnel-realistic values sit around 10 for aerospace, up to a few hundred for internal flows; anything approaching 1 000+ means the inlet is already fully turbulent before the body sees it.',
      turbulenceIntensity:
        'Turbulence intensity declared on the inlet BC. Aerospace tunnels around 0.1–1 %, industrial flows 5–10 %. This is an input, not a measurement; carried here so the derived ν_t / ν is auditable.',
      turbulentLengthScale:
        'Turbulent length scale declared on the inlet BC; typically a fraction of the hydraulic diameter or the characteristic body length. Carried here so the derived ν_t / ν is auditable.',
      portArea:
        'Cross-sectional area of the inlet / outlet face (m²). Compare against the design intent; a 4× port-area mismatch surfaces here as an unexpected mass balance, not as a broken solve.',
      reversedFlowFrac:
        'Area fraction of the outlet face where flow is entering the domain (backflow). An area-averaged outlet velocity hides backflow through sign cancellation; this per-face flux-sign split is the metric that surfaces it.',
      stagnantVolumeFrac:
        'Fraction of the domain volume where |U| falls below a fixed floor. High values (a HX example ran at 92%) mean the working fluid is not reaching most of the geometry; the run "succeeded" without doing anything.',
      deadEndVolumeFrac:
        'Fraction of the domain volume that is a geometric cul-de-sac (no flow-through path). Computed from mesh connectivity rather than the flow itself, so it stays valid on a diverged run.',
      disconnectedVolumeFrac:
        'Fraction of the mesh volume with no path at all to an inlet or outlet — fluid the flow can never reach, usually a void the mesher sealed off rather than a feature of the part. Read from the mesh alone, so it stays valid on a diverged run. A blind branch the flow can still enter is not counted here; that is the dead-end row.',
      hydraulicDiameter:
        'Hydraulic diameter D_h = 4 · A / P at the inlet, where A is the flow area and P the wetted perimeter. Auditable form of the length scale every internal-flow Reynolds number rests on.',
      reynolds:
        'Reynolds number Re = ρ U D_h / μ at the inlet, derived. Absent from the wire today; belongs alongside the inlet BC so the inlet condition is auditable.',
    },
    internalFlowHeading: 'Internal-flow signals',
    pressureDropKinematic: 'Δp / ρ',
    wallPressureRange: 'Wall pressure (min / max)',
    reversedFlowFrac: 'Reversed-flow fraction per outlet',
    stagnantVolumeFrac: 'Stagnant volume fraction',
    deadEndVolumeFrac: 'Dead-end volume fraction',
    disconnectedVolumeFrac: 'Sealed-off volume fraction',
    reynolds: 'Reynolds',
    internalFlowExtendedPending:
      'Dead-end volume — a blind branch the flow can enter but never pass through — is still under development. An empty value on that row is not the same as "no dead ends".',
  },
  handCalc: {
    scopes: {
      sectionHeading: 'Local region',
      unavailableCount: '{{count}} not available here',
      noneValid: 'No relation this run computes applies to this scope.',
      runReports: 'run reports',
      waitingOn: 'waiting on',
      facts: {
        declaredPower: 'declared',
        interfacePower: 'through interfaces',
        massFlow: 'ṁ',
        coolantRise: 'ΔT',
        pressureDrop: 'Δp',
        patchFlux: 'flux',
        wettedArea: 'A_wet',
        volume: 'volume',
        peakTemperature: 'T peak',
        patchSum: 'patches summed',
        peakFlux: 'peak flux',
        patchGap: 'breakdown gap',
        fluxDensity: 'Q̇/A_wet',
        shareOfTotal: 'of reported total',
        spread: 'interior spread',
        interfaceDeltaT: 'ΔT (region means)',
        overallCoefficient: 'K = Q̇/(A·ΔT)',
        meanTemperature: 'T mean',
        peakVelocity: 'U peak',
        resistance: 'R',
        closure: 'closure gap',
      },
      resolvedFrom:
        'Resolved from the {{region}} region — material {{material}}. The assumptions column above is case-wide; this scope reads its own.',
      energyFlow: 'heat path',
      reversedPatches_one:
        '{{count}} interface carries heat the other way — into the solid.',
      reversedPatches_other:
        '{{count}} interfaces carry heat the other way — into the solid.',
      conductionUnavailable:
        'Solid-solid conduction is not on this run — every contact between two solids is unmeasured, so a solid touching only the coolant is drawn with just that one band.',
      nodeTemperatures: 'T min / avg / max',
      tempStops: { min: 'min', avg: 'avg', max: 'max' },
      nodeIn: 'in',
      nodeOut: 'out',
      kMissing: {
        noArea: 'K needs a wetted area, which this run does not report',
        noGradient:
          'K needs a gradient — the two sides are at the same temperature',
      },
      railLabel: 'Hand-calc scopes',
      heading: 'scope',
      filed: '{{count}} filed',
      wholeModel: 'whole model',
      noScopes: 'This run reports no scopes to file a hand calc against.',
      kinds: {
        case: 'case',
        fluid: 'fluid',
        solid: 'solid',
        iface: 'interface',
      },
      kindPlural: {
        case: 'cases',
        fluid: 'fluid regions',
        solid: 'solid regions',
        iface: 'interfaces',
      },
      kindWords: {
        case: 'the whole case',
        fluid: 'a fluid region',
        solid: 'a solid region',
        iface: 'an interface',
      },
      ifaceNote:
        'Interfaces exist because two regions touch, so they are not created by hand. A convection relation needs a fluid on one side and is refused on a solid-solid contact; Fourier conduction applies there instead.',
      openAdd: 'add relation',
      addDialogLabel: 'Relations available on {{where}}',
      closeAdd: 'close',
      addAction: 'add',
      validOn: 'valid on {{where}}',
      validNote: '{{valid}} of {{total}} apply here',
      allFiled: 'Everything valid on this scope is already filed.',
      // Marks an INVERSION in the add menu — a row solved against a target
      // the reader types, rather than computed from what the run reports.
      backsolveTag: 'backsolve',
      // On the chip. Names what the row solves FOR, and says the equation
      // beside it is read in reverse — that is the whole distinction between
      // this and a plain relation, and it decides what an empty value means.
      backsolveTagTooltip:
        'Solved backwards. You state the target you need and this inverts the equation beside it to give the {{name}} that would reach it. An empty result means no target stated yet — not a figure the run failed to report.',
      // Under the menu header, whenever the list offers at least one. The chip
      // tooltip cannot be reached by keyboard (it sits inside the row button),
      // so this line is where that reader gets the same fact.
      backsolveLegend:
        'A backsolve row runs the equation in reverse: you state the target, it returns the input needed to reach it.',
      // On the chip. Names what the row solves FOR, and says the equation
      // beside it is read in reverse — that is the whole distinction between
      soleRegion: '{{kind}} only',
      unavailable: 'not available here',
      unavailableHint: 'shown with the reason, never silently missing',
      emptyFiled:
        'Nothing filed on this scope yet. Add a relation to work it here.',
      remove: 'Remove from this scope',
      removeNamed: 'Remove {{name}} from this scope',
      reasons: {
        needsFluid: 'needs a flow — file it on a fluid region',
        needsSolid: 'needs a body — file it on a solid region',
        needsWettedWall:
          'needs a fluid against the wall — this contact is solid to solid',
        needsWall: 'needs a wall — file it on an interface',
        needsWholeCase: 'spans the whole path — file it on the whole model',
        ambiguous:
          'ambiguous across {{count}} {{kind}} — file it on one of them',
      },
    },
    relations: {
      resultUnitLabel: 'Result unit',
      feaInputs: {
        heading: 'Assumptions',
        derivedHeading: 'Relations',
        resetLabel: 'Reset assumptions',
        resetTooltip:
          'Restore every value seeded from the run and the material library.',
        tooltips: {
          volume:
            'Part volume as the run reported it. On a run recorded before that was published it falls back to m / ρ, which is exact only for a single-material part — the solver builds mass from a per-body density, so no single ρ inverts it on an assembly. Override it if you know the volume.',
          area: 'Effective section: a back-solve of volume over length, not a measurement. Override it with the real cross-section if you have it.',
          fibre:
            'Distance from the neutral axis to the extreme fibre. Seeded for a round section; override it for the real one.',
          inertia:
            'Second moment of area. Seeded as A²/4π for a round section — a square would be A²/12. Do NOT back it out of radius_of_gyration: that is mass-based and length-dominated, not the bending stiffness of the section.',
          mass: "The run's own mass, from result_summary.mass_kg. Seeds the volume, and through it every section property below.",
          length:
            'The longest span. The spec sources it from the mesh bounding box, which this panel does not fetch — type it to unblock the chain.',
          load: "Total applied load: the magnitude summed over the run's force boundary conditions. A modal or thermal run declares none, and this box stays empty.",
          density:
            "Density, from the material library entry for the run's declared material. Override it to try a different alloy without re-running.",
          e: "Young's modulus, from the same library entry.",
          yieldStrength:
            'Yield strength, from the same library entry. The factor of safety is quoted against it.',
        },
      },
      scope: {
        fea: 'STRUCTURAL · FEA',
        cht: 'CONJUGATE HEAT TRANSFER · CHT',
        external: 'EXTERNAL FLOW · CFD',
        internal: 'INTERNAL FLOW · CFD',
      },
      names: {
        fea: {
          volume: 'Volume',
          length: 'Characteristic length',
          area: 'Effective section',
          fibre: 'Extreme fibre',
          inertia: 'Second moment of area',
          axialStress: 'Axial stress',
          bendingStress: 'Bending stress',
          deflection: 'Deflection',
          extension: 'Axial extension',
          fos: 'Factor of safety',
          buckling: 'Euler buckling load',
          frequency: 'First natural frequency',
          slenderness: 'Slenderness',
          eulerEndFixity: 'Euler end-fixity',
          cantilever: 'Cantilever',
          simplySupported: 'Simply supported',
          fixedFixed: 'Fixed–fixed',
          pinnedPinned: 'Pinned–pinned',
          fixedFree: 'Fixed–free',
          beamBending: 'Beam bending',
          axialRod: 'Axial rod',
        },
        dynamicPressure: 'Dynamic pressure',
        reynolds: 'Reynolds number',
        skinFriction: 'Skin friction',
        frictionVelocity: 'Wall friction velocity',
        firstCellHeight: 'First cell height',
        backSolvedCd: 'Solver C_D',
        handDrag: 'Hand drag',
        backSolvedCl: 'Solver C_L',
        handLift: 'Hand lift',
        refAreaFromCd: 'A_ref from C_D',
        refAreaFromCl: 'A_ref from C_L',
      },
      validity: {
        cantilever: 'one end fixed, load at the free tip',
        simplySupported: 'both ends pinned, load at centre',
        fixedFixed: 'both ends built in',
        pinnedPinned: 'both ends pinned — the reference case',
        fixedFree: 'one end built in, the other free',
        beamBending: 'slender, bending-dominated',
        axialRod: 'stocky, longitudinal mode',
      },
      aria: {
        dynamicPressure: 'q equals one half rho times U inlet squared',
        reynolds: 'Reynolds number equals U inlet times L over nu',
      },
      notes: {
        feaBendingMoment:
          'Maximum moment for the end condition picked on the deflection relation — F·L at a cantilever root, F·L/4 at the centre of a simply supported span.',
        feaFos:
          'Against the governing stress — bending where it exists, else axial.',
        feaBuckling:
          'Slender members in compression only. Compare the margin P_cr / F.',
        feaFrequency:
          'A beam is a bluff model of a 3-D part, so expect order-of-magnitude agreement rather than a close match.',
        feaSlenderness:
          'High λ means bending and buckling dominate; low λ means bulk or axial behaviour.',
        skinFriction:
          'Flat-plate turbulent correlation. Past a strong pressure gradient or a separated wake this under-reads.',
        backSolved:
          "Back-solved from the run's own force, so it is what the solver reports rather than something to check it against.",
        backSolvedArea:
          'Back-solved from the solver force and the coefficient you typed — a check on the reference area the run declared.',
      },
      indexLabel: 'Relations in this hand-calc',
      count_one: '{{count}} relation',
      count_other: '{{count}} relations',
      given: 'Given',
      result: 'Result',
      references: 'Compare against',
      referencesShort: 'reference forms below',
      referenceOutOfRange:
        "Outside this form's published range at the run's own Re and Pr — shown so you can see it does not apply, not to be quoted.",
      referencesHint:
        'Published forms, read-only. Apply the one that matches your case by hand and compare it against the result above — this panel does not evaluate them.',
      noCounterpartShort: 'idealization step',
      resultUnavailable:
        'Every input is present, but one of them is outside the range this relation is defined over, so it has no value.',
      runFigureMissing:
        'This run does not report a figure this step needs, so there is nothing to substitute. Not something you can fill in above.',
      dimensionlessSuffix:
        '{{symbol}} — a dimensionless quantity, so this box takes a plain number with no unit. A ratio is typed as a fraction; `1%` is also accepted and stores 0.01.',
      fromEarlierStep:
        'Produced by an earlier step of this derivation, so there is no field for it here — fill that step in and this follows. A dash means that step could not be computed yet.',
      fromEarlierStepTag: '↑ step',
      substituteBlocked:
        'An input above is missing, so this step cannot be worked.',
      pickOne: 'Correlation · pick one',
      comparesTo: 'compares to {{key}}',
      noCounterpart: 'idealization step — nothing to compare against',
      empty: 'No hand-calc relations for this template yet.',
    },
    heading: 'Hand-calc sandbox',
    subhead:
      "Plug in the reference values you expected the run to sit at. Fields seed from the solver's own inlet BC + fluid preset + reference geometry; edit any to see how the derived numbers move.",
    dpSelectorLabel: 'Reference design point',
    dpItem: 'DP #{{index}} · {{label}}',
    resetLabel: 'Reset to submission values',
    resetTooltip:
      "Reset every field back to the values the run was submitted with: solver inlet BC, fluid preset, force-coefficient reference area / chord, and the mesh-derived inlet area on internal-flow runs. Units snap back to SI so the numeric field matches what the case actually ran on. Your edits persist across DP switches; press this to re-sync to the current study's submission.",
    assumptions: 'Assumptions',
    target: 'target',
    expected: 'expected',
    firstLayer: 'first-layer height',
    derivedHeading: 'Derived',
    shapePresetLabel: 'Prefill {{coefficient}} from a common shape',
    shapePresetTitle: {
      cd: 'Typical C_D values',
      cl: 'Typical C_L values',
    },
    shapePresetSubtitle: 'Click a row to prefill',
    shapePreset: {
      sphereSmooth: 'Sphere (smooth)',
      sphereSmoothRegime: 'Supercritical Re, past the C_D drop (Re > 4×10⁵)',
      sphereRough: 'Sphere (rough)',
      sphereRoughRegime:
        'Golf ball, tripped BL, ~10× the smooth Re range at same C_D',
      cube: 'Cube',
      cubeRegime: 'Flow parallel to a face, Re > 10⁴',
      flatPlatePerp: 'Flat plate, perpendicular',
      flatPlatePerpRegime: 'Bluff body limit',
      cylinderLong: 'Cylinder (long, cross-flow)',
      cylinderLongRegime: 'Aspect ratio > 10, subcritical Re ~10³–10⁵',
      streamlinedHalfBody: 'Streamlined half-body',
      streamlinedHalfBodyRegime: 'Rounded nose, tapered tail',
      streamlinedBody: 'Streamlined body (fish-shape)',
      streamlinedBodyRegime: '4:1 fineness ratio, attached BL',
      teardropAxisym: 'Axisymmetric teardrop',
      teardropAxisymRegime: 'Ideal streamlined shape, Re > 10⁵',
      sedanTypical: 'Passenger sedan',
      sedanTypicalRegime: 'Post-2010 production car',
      sportsCar: 'Sports car',
      sportsCarRegime: 'Low-hood production coupe',
      suvTypical: 'SUV / crossover',
      suvTypicalRegime: 'Boxy production SUV',
      semiTruck: 'Semi-truck tractor',
      semiTruckRegime: 'Class 8 tractor + trailer',
      naca0012_0deg: 'NACA 0012 airfoil, α = 0°',
      naca0012_0degRegime: 'Symmetric, no lift, Re ~5×10⁶',
      naca0012_10deg: 'NACA 0012 airfoil, α = 10°',
      naca0012_10degRegime: 'Near stall, Re ~5×10⁶',
      naca2412_0deg: 'NACA 2412 airfoil, α = 0°',
      naca2412_0degRegime: 'Cambered, positive lift at zero AoA, Re ~5×10⁶',
      naca2412_10deg: 'NACA 2412 airfoil, α = 10°',
      naca2412_10degRegime: 'Well below stall, Re ~5×10⁶',
      flatPlateThinAoA5: 'Thin flat plate, α = 5°',
      flatPlateThinAoA5Regime: 'Thin-airfoil theory: C_L ≈ 2π·α (rad)',
    },
    dynamicPressure: 'q = ½ρU²',
    backSolvedCd: 'C_D back-solved from solver drag',
    backSolvedShort: 'back-solved from solver drag',
    expectedDrag: 'D expected from C_D',
    expectedFromCd: 'expected from C_D',
    tooltips: {
      rho: 'Fluid density (kg/m³). Seeded from the solver fluid preset or an explicit density override. 1.204 = air at 20°C, 998 = water at 20°C.',
      nu: 'Kinematic viscosity ν = μ/ρ (m²/s). Seeded from the solver fluid preset or an explicit viscosity override. Feeds Re and the y⁺ correlation.',
      velocity:
        'Freestream / inlet velocity magnitude U∞ (m/s). Seeded from the solver inlet BC, with the reported inlet-averaged velocity as a fallback.',
      refArea:
        'Reference area A_ref (m²) used for C_D / C_L. Seeded from the solver force-coefficient reference area; the value the solver actually divided by. Changing it back-solves the coefficient the run would have reported under a different A_ref.',
      refChord:
        'Reference length l_ref (m). Doubles as the plate length in the y⁺ / boundary-layer Re here. For an airfoil this is the chord; for a bluff body the streamwise extent.',
      yPlus:
        'Target dimensionless wall distance for the first cell. 1 for a wall-resolving low-Re model, 30–100 for wall functions. The panel back-solves the first-layer height that would land y⁺ on this target for the current U∞ and l_ref.',
      handCd:
        'Expected drag coefficient; from a wind-tunnel measurement, a Cd–Re correlation, or a target. Entering it turns on the drag comparison row in the summary table below.',
      handCl:
        'Expected lift coefficient; same wind-tunnel / correlation / target sources as C_D. Entering it turns on the lift comparison row against the solver Y-axis force integral.',
      inletArea:
        'Inlet cross-sectional area A_in (m²). For internal flow this is what drives the Reynolds number (Re = ρU·D_h/μ) via the port hydraulic diameter, not a body reference area. Seed from the mesh preflight when available.',
      hydraulicDiameter:
        'Hydraulic diameter D_h = 4·V / A (m) at the inlet port. On a circular pipe this is just the diameter; on a rectangular duct it is 2·a·b / (a+b). For internal flow this replaces the airfoil chord as the Re length scale.',
      derivedQ:
        'Dynamic pressure q = ½ρU² (Pa). What a Pitot tube would read on the stagnation streamline minus the static pressure. Used as the normalising factor for every force / pressure coefficient.',
      derivedRe:
        'Reynolds number Re = U·L/ν = ρ·U·L/μ. Ratio of inertial to viscous forces. The 1/7-power-law Cf correlation below is valid for a smooth turbulent boundary layer, roughly 5×10⁵ < Re < 10⁷; outside that it under-predicts skin friction.',
      derivedCf:
        'Skin-friction coefficient Cf = 0.026·Re^(−1/7). 1/7-power-law correlation for a flat-plate turbulent BL; cheap and reasonable for external aero at Re ~ 10⁶. Not appropriate for laminar (Blasius), pipe flow (use Moody / Colebrook), or roughness-dominated surfaces.',
      derivedUTau:
        'Friction velocity uτ = U·√(Cf/2) = √(τ_wall / ρ) (m/s). Sets the near-wall velocity scale. Combined with ν, it fixes y⁺ = y·uτ/ν and therefore the first-layer height for any target y⁺.',
      derivedY0:
        'First-cell wall-normal height y₀ = y⁺·ν / uτ (converted to mm here). Plug in your target y⁺ and this is the first-layer height the mesh needs so the log-law (or wall-resolving model) sits where it should.',
      derivedBackCd:
        'C_D = D / (½ρU²·A_ref). Reverse of the drag definition: given the solver drag along X and the reference area, this is the coefficient the run would have reported. Useful when the force-coefficient extraction was not enabled on the solve.',
      derivedExpectedD:
        'D = C_D·½ρU²·A_ref (N). Forward drag from your expected C_D. Comparing this against the solver drag surfaces whether the deviation is in the coefficient or in the reference area.',
      derivedBackCl:
        'C_L = L / (½ρU²·A_ref). Same inverse pair as the C_D row, but against the solver Y-axis force. Useful when the run reported no force-coefficient extraction.',
      derivedExpectedL:
        'L = C_L·½ρU²·A_ref (N). Forward lift from your expected C_L. A Δ against the solver Y force reveals whether the coefficient or the reference area is off.',
      derivedBackARefFromCd:
        "A_ref = D / (½ρU²·C_D) (m²). Sanity check for the A_ref input above: given the solver's drag along X and your expected C_D, this is the reference area those two would agree on. Compare against the A_ref you entered; the classic misconfig is the 1 m² fallback default silently reaching the solver. Only shown when both C_D and the solver drag are available.",
      derivedBackARefFromCl:
        "A_ref = L / (½ρU²·C_L) (m²). Same sanity check as the C_D row, against the solver's Y force + your expected C_L. Only shown when both C_L and the solver lift are available.",
    },
  },
  summary: {
    heading: 'Self-consistency check',
    subhead:
      'Arithmetic agreement between the hand-calc panel and the solver only; every row is an identity that must match to rounding (a Δ here is a plumbing bug). The Verification section is where actual trustworthiness signals live.',
    quantity: 'Quantity',
    hand: 'Hand',
    solver: 'Solver',
    absDelta: 'Δ',
    pctDelta: 'Δ %',
    dragX: 'Drag X (pressure + viscous, streamwise)',
    liftY: 'Lift Y (pressure + viscous, cross-stream)',
    dynamicPressure: 'q = ½ρU²',
    caption:
      'Rows with a missing side render "—"; either the sandbox has no value yet or the solver did not emit the field.',
    tooltips: {
      cd: 'Drag coefficient. Solver = the value the run reported. Hand = your expected C_D input. A high Δ % is either a genuine over/under-prediction or a mismatched A_ref between the two; check the reference-area row in the hand-calc panel above.',
      cl: 'Lift coefficient. Solver = the value the run reported. Hand = your expected C_L input. Same diagnostic pattern as C_D; a high Δ is usually a real prediction gap or an A_ref mismatch.',
      liftY:
        'Cross-stream force = pressure component + viscous component along the lift axis. Solver = force_pressure_y + force_viscous_y from the run. Hand = C_L_expected · ½ρU²·A_ref.',
      dragX:
        'Streamwise force = pressure component + viscous component along the flow axis. Solver = force_pressure_x + force_viscous_x from the run. Hand = C_D_expected · ½ρU²·A_ref.',
      dynamicPressure:
        'Dynamic pressure q = ½ρU². Both sides compute this from the same ρ and U∞ inputs in the sandbox, so this row should always match at 0%; a non-zero Δ means the two sides read different density or velocity values.',
      pressureDrop:
        'Physical pressure drop across the channel, p_in − p_out. Solver = the value the run reported. Hand-side value is intentionally blank in v1; a target Δp input is a follow-up on the sandbox.',
      pressureDropKinematic:
        'Kinematic pressure drop (Δp / ρ). What the solver writes internally; the physical Pa row above is this multiplied by ρ.',
      inletFlow:
        "Volumetric flow through the inlet |Q_in| = |U_in·A_in|. Hand = your U∞ · A_in from the sandbox. Solver = the run's inlet-integrated flow rate (reported signed against the outward face normal; the absolute value is compared here). A Δ % near 0 is a passing mass-balance check.",
      inletVelocity:
        "Area-averaged velocity at the inlet patch. Hand = the U∞ you entered. Solver = the run's inlet-integrated velocity. Divergence between the two implies the actual inlet area is not what the sandbox assumes; check A_in.",
    },
  },
  radar: {
    heading: 'L1 · Self-consistency check',
    subhead:
      'L1 in the V&V ladder; arithmetic agreement between the hand-calc panel and the solver only. L2 (surrogate / DOE), L3 (verification), L4/L5 (measured; tunnel / flight) drop on as new layers when their imports land, each with its own tolerance band. Score is worst-axis only. A run can pass every arithmetic identity here and still be non-physical, so read this as a self-test only.',
    pending: 'not supported yet',
    // Empty-state copy shown INSIDE the chart box when the polygon
    // would collapse to the origin — either the run hasn't produced
    // completed DPs yet, or the reader hasn't filled the hand-calc
    // panel above so no axis has both sides. Mirrors Section4Summary's
    // pattern of showing structure with a "—" fallback rather than
    // going blank.
    emptyNoCompleted: 'No completed runs to score against yet.',
    emptyNoScores:
      'Fill the Hand-Calc panel above to score self-consistency; each row that gets a value adds an axis to the radar.',
    worstAxisScoreTooltip:
      '0% = the worst-scoring axis is on or beyond the outer edge of its tolerance band (1% for identities, 30% for correlations). 100% = every scored axis matches to rounding. Identities are widened from the pure-arithmetic 0.1% to 1% because area-integrated fields (|Q_in|, U_in,avg, streamwise drag) carry finite-precision jitter that would falsely tank the axis. The reported figure is the MINIMUM across scored axes, not an average; one catastrophic axis is never averaged away by five good ones. Placeholder axes (drawn as gaps) are excluded from the score. Median and Best sit alongside as a shape-of-the-distribution glance; Worst stays the headline.',
    symbol: 'Symbol',
    identityBand: 'identity · 1% band',
    correlationBand: 'correlation · 30% band',
    worstAxisScoreCaption: 'Scored-axis distribution',
    worstLabel: 'Worst',
    medianLabel: 'Median',
    maxLabel: 'Best',
    hand: 'Hand',
    solver: 'Solver',
    score: 'Score',
    placeholderTooltip:
      'Signal not supported yet. Rendered as a gap on the polygon rather than a value so the missing axis never reads as pass or fail.',
    series: {
      selfConsistency: 'Hand vs solver',
    },
    legendCaption: 'Design points',
    legendPinTooltip: 'Click to pin; highlight sticks while you edit',
    legendUnpinTooltip: 'Click to unpin',
    symbolAnnotations: {
      pressureViscous: 'pressure + viscous',
    },
    axes: {
      cd: 'Drag coefficient',
      cl: 'Lift coefficient',
      dragX: 'Streamwise drag force',
      liftY: 'Cross-stream lift force',
      q: 'Dynamic pressure',
      qIn: 'Inlet flow rate',
      uIn: 'Inlet velocity',
      yPlusBands: 'y⁺ coverage',
      nutRatio: 'Turbulent viscosity',
      massBalance: 'Mass balance',
      deadVolume: 'Stagnant fraction',
      blockage: 'Blockage ratio',
    },
    tooltips: {
      cd: 'Drag coefficient; solver value vs your expected input. 30% tolerance band because a large spread on a non-flat-plate body is legitimate physics for a flat-plate correlation, not a run defect.',
      cl: 'Lift coefficient; solver value vs your expected input. 30% band, same rationale as C_D.',
      dragX:
        'Streamwise force identity; hand-computed drag from your C_D, ρ, U, A_ref against the solver-reported pressure + viscous force along the flow axis. Tight 1% band; a Δ here indicates a plumbing / reference-value mismatch.',
      liftY:
        'Cross-stream force identity; hand-computed lift from your C_L, ρ, U, A_ref against the solver-reported pressure + viscous force along the lift axis. Tight 1% band.',
      q: 'Dynamic pressure identity; both sides compute q = ½ρU² from the same ρ and U. Δ should be zero to rounding; anything else means the two sides are reading different inputs.',
      qIn: 'Inlet flow-rate identity; hand U∞·A_in against the solver inlet-integrated flow. Tight 1% band; a Δ points at an A_in mismatch or a reversed inlet BC.',
      uIn: "Inlet velocity identity; hand U∞ against the run's area-averaged inlet velocity. 1% band.",
      yPlusBandsPending:
        'Area-fraction coverage of the wetted wall inside the target y⁺ band (<5 / 5-30 / 30-100 / >100); not supported yet. Once available, this axis will score how much of the wall sits in the log-law-valid range.',
      nutRatioPending:
        'Turbulent viscosity ratio percentiles (P10 / P50 / P90); not supported yet. Once available, this axis will flag runs where the turbulence model is barely contributing (P50 below 1 indicates a laminar-like solve under a turbulence template).',
      massBalancePending:
        'Direct mass-balance fraction; not supported yet on this template as a first-class field (derivable indirectly today from |Q_in| ≈ |Q_out|). Placeholder until the dedicated field ships.',
      deadVolumePending:
        'Fraction of the fluid domain that never sees appreciable flow; not supported yet. Catches the failure mode where a large fraction of the domain is stagnant while the run still reports success.',
      blockagePending:
        'Blockage ratio (projected frontal area / enclosure cross-section); not supported yet. Will flag under-sized enclosures on external-flow runs where the walls constrict the free stream.',
    },
  },
  surrogate: {
    loocvSummary:
      'LOOCV at iteration {{iteration}} across {{runs}} completed runs: {{metrics}}',
  },
  chtViz: {
    regions: 'Regions',
    regionLocked: 'At least one region stays visible.',
    uncoloured: 'Grey: no {{field}} on {{regions}}',
    uncolouredMore_one: '{{count}} more',
    uncolouredMore_other: '{{count}} more',
    noFiles: 'No visualization files for this run yet.',
    noSurface: 'No surface file for this region yet.',
  },
  viewer: {
    extremaToggle: 'Max/min',
    extremaMax: 'Max:',
    extremaMin: 'Min:',
    extremaMaxName: 'Maximum',
    extremaMinName: 'Minimum',
    extremaA11y: '{{name}} {{quantity}}: {{value}}',
    extremaMoveHint: 'Arrow keys move this label.',
    extremaTied_one: '(+{{n}} tied)',
    extremaTied_other: '(+{{n}} tied)',
    extremaOnSlice: 'on slice',
    extremaBehind: 'behind the model',
    extremaOffView: 'outside the view',
    lockRange: 'Lock colour range',
    rangeLocked: 'Range locked',
    rangeClamped: 'Peak exceeds locked range',
    rangeMin: 'Range minimum',
    rangeMax: 'Range maximum',
    fitToVisible: 'Fit to visible',
    sliceLoading: 'Loading slice…',
    sliceFieldUnavailable: 'The slice does not carry {{field}}.',
    field: 'Field',
    visibilityShown: '{{label}}: {{shown}} of {{total}} shown',
    colormap: 'Colormap',
    units: 'Units',
    loadConditions: 'Load conditions',
    boundaryConditions: 'Boundary conditions',
    volumeSurface: 'Volume surface',
    // Distinct from the always-on bounding box (the enclosure outline
    // painted for external_flow via `vtkOutlineFilter`, no toggle).
    // "Volume surface" is the decimated fluid-domain surface polydata
    //; includes body walls for external_flow; colored by the active
    // field. Uncheck to see streamlines / hotspots against transparent bg.
    volumeSurfaceTooltip:
      'The decimated flow-domain surface, colored by the active field. For external flow this includes the body walls; the enclosure outline is a separate always-on box. Uncheck to see streamlines / hotspots without the surface occluding them.',
    bodyOpacity: 'Body',
    streamlines: 'Streamlines',
    streamlineDensity: 'Lines',
    streamlineDensityName: 'Lines shown',
    streamlineDensityCount: '{{drawn}} of {{total}} lines',
    streamlinesTooltip:
      "Seeded streamlines through the fluid domain; colored by |U|, independent of the active field. Shows the flow topology (attached / separated / recirculating). Available on runs where the solver's streamlines output ran.",
    hotspots: 'Hotspots',
    hotspotsTooltip:
      'Peaks of the wall-side scalar fields (pressure extrema, hot / cold spots, high y⁺, high ν_t/ν). Rendered as tagged spheres on the surface; filter by field with the chips below.',
    fields: 'Fields',
    pick: {
      heading: 'Pick',
      close: 'Close pick readout',
      position: 'Position',
      patch: 'Patch',
      cellId: 'Cell #',
    },
    // Colormap presets; keyed by the exact `ColormapPreset` string so
    // the `SelectItem` render can look them up directly. Matplotlib
    // names (Viridis / Plasma / Inferno) stay unchanged in every locale
    // because they're proper names used verbatim in scientific
    // literature; only the descriptive presets get translated.
    customColors: {
      custom: 'Custom',
      edit: 'Edit colors',
      title: 'Color set',
      description:
        'Choose a color set, or select Custom to change the low, middle, and high colors. The model updates when you close this window.',
      saveFailed: 'Colors are not saved. Try again.',
      preview: 'Color preview',
      low: 'Low',
      middle: 'Middle',
      high: 'High',
      loading: 'Loading saved colors…',
      loadFailed: 'Could not load saved colors.',
      saving: 'Saving colors…',
      saved: 'Colors saved',
      temporary: 'Colors apply to this page only.',
      idle: '',
    },
    colormapPresets: {
      'Cool to Warm': 'Cool to Warm',
      'Viridis (matplotlib)': 'Viridis (matplotlib)',
      'Plasma (matplotlib)': 'Plasma (matplotlib)',
      'Inferno (matplotlib)': 'Inferno (matplotlib)',
      'Black-Body Radiation': 'Black-Body Radiation',
      'X Ray': 'X Ray',
    },
    aboutField: 'About {{field}}',
    aboutTemperature: 'About temperature',
    loadingVisualization: 'Loading visualization…',
    // ENG-3097 — what the 3D loader is doing, and how far it has got.
    loadStage: {
      listing: 'Finding result files…',
      fetching: 'Downloading results…',
      parsing: 'Reading results…',
      building: 'Building the 3D view…',
    },
    loadBytes: '{{loaded}} of {{total}}',
    loadBytesSoFar: '{{loaded}} so far',
    volumeUnavailable: 'Volume surface unavailable: {{message}}',
    streamlinesUnavailable: 'Streamlines unavailable: {{message}}',
    streamlinesTooShort:
      'The solver’s streamlines are far too short to see at this scale, so there is nothing visible to draw. That points at the run, not this view.',
    streamlinesEmpty:
      'This run produced no streamlines, so there is nothing to draw here.',
    streamlinesStillborn:
      'The solver’s streamlines leave the domain almost immediately, so there is nothing visible to draw. That points at the run, not this view.',
    hotspotsUnavailable: 'Hotspots unavailable: {{message}}',
    slice: 'Slice',
    sliceTooltip:
      'Cut through the fluid volume with an axis-aligned plane. Colour ramp adjusts to the visible slice so it does not wash out on one hot cell.',
    sliceAxis: 'Axis',
    slicePosition: 'Position',
    sliceUnavailable: 'Slice unavailable: {{message}}',
    clip: 'Section',
    clipTooltip:
      'Cut every boundary patch and the volume surface with a shared plane so you can look inside the geometry. Complements the volume slice: this cuts the surfaces themselves, so studies without a resampled field still get a section view.',
    clipAxis: 'Axis',
    clipPosition: 'Position',
    clipFlip: 'Flip',
    clipFlipTooltip: 'Keep the other half of the cut.',
    probeLine: 'Probe line',
    probeLineTooltip:
      'Show the line the probe chart is sampling along, drawn through the geometry it cuts. Hide it when the line crosses exactly the part of the body you are trying to look at.',
    boundaryPatch_one: '{{count}} boundary patch',
    boundaryPatch_other: '{{count}} boundary patches',
    showLess: 'show less',
    showMore: '+{{count}} more',
    hotspotsCount: 'Hotspots ({{count}})',
    hotspotsShowAll: 'Show all {{count}}',
    hotspotsShowLess: 'Show less',
    failedVisualization: 'Failed to load visualization',
    failure: {
      notReadyTitle: 'Results are not ready yet',
      notReadyBody:
        'This run has not written its results. If it is still going, the 3D view appears here when it finishes.',
      notReadyNotice: 'this run has not written it yet',
      linkExpiredTitle: 'This page has been open too long',
      linkExpiredBody:
        'Links to result files last an hour. Reload the page to get a fresh one.',
      linkExpiredNotice: 'the link expired — reload the page for a fresh one',
      artifactMissingTitle: 'A result file is missing',
      artifactMissingBody:
        'This run lists a file that is no longer stored. Reload the page, and let us know if it keeps happening.',
      artifactMissingNotice:
        'its file is no longer stored — let us know if it keeps happening',
      forbiddenTitle: 'You do not have access to these results',
      forbiddenBody:
        'Ask the owner of this study for access. Everything else you can see is what your current access allows.',
      forbiddenNotice: 'your access does not cover it',
      noResultsTitle: 'No results to show',
      noResultsBody:
        'There is nothing for the 3D view to draw. The numbers and charts for this run are unaffected.',
      noResultsNotice: 'this run produced none',
      fetchFailedTitle: 'Could not load the results',
      fetchFailedBody:
        'The result files could not be downloaded. Reload the page, and let us know if it keeps happening.',
      fetchFailedNotice: 'it could not be downloaded — reload the page',
      unreadablePayloadTitle: 'Results are in an unexpected format',
      unreadablePayloadBody:
        'This run’s visualization data is not in a format this viewer reads. Let us know — this run’s other results are unaffected.',
      unreadablePayloadNotice: 'it is not in a format this viewer reads',
      loadStalledTitle: 'Loading has stalled',
      loadStalledBody:
        'Nothing has arrived for a while. Retry; if it stalls again, this result may be too large for the browser to draw — its numbers and charts are unaffected.',
      loadStalledNotice: 'loading stalled — retry',
    },
  },
  viewerControls: {
    showControls: 'Show viewer controls',
    mouse: 'Mouse',
    dragL: 'Drag L',
    tumble: 'Tumble (yaw + pitch)',
    dragR: 'Drag R',
    pan: 'Pan',
    dragLR: 'Drag L + R',
    roll: 'Roll',
    wheel: 'Wheel',
    zoom: 'Zoom toward cursor',
    rightClick: 'Right-click',
    contextMenuSuppressed: 'Context menu suppressed',
    ctrlL: 'Left click (hold still)',
    pick: 'Pick; read out the surface at the cursor',
    keyboard: 'Keyboard',
    lookAxis: 'Look down X / Y / Z axis',
    pitch: '±90° pitch',
    yaw: '±90° yaw',
    rollKey: '±90° roll',
    slice: 'Section clip along the current view (not yet wired)',
    toggleSlice: 'Toggle the volume slice',
    resetView: 'Reset the camera to the default view',
  },
  guard: {
    // SimErrorCode labels for a guard row titled by the code the backend
    // flagged. Short noun phrases, not sentences: the row's detail column
    // carries the count and the reason prose lives in the guard panel.
    simErrorCode: {
      divergencePressure: 'Pressure diverged',
      divergenceVelocity: 'Velocity diverged',
      divergenceTurbulence: 'Turbulence diverged',
      courantNumberExceeded: 'Courant number exceeded',
      nanInField: 'NaN in a field',
      sigfpeInField: 'Floating-point fault in a field',
      massImbalanceHigh: 'Mass imbalance high',
      continuityErrorHigh: 'Continuity error high',
      wallYplusOutOfRange: 'Wall y⁺ out of range',
      solidTRanAway: 'Solid temperature ran away',
      interfaceUncoupled: 'Interface uncoupled',
      heatSourceNoTarget: 'Heat source has no target',
      inletOutletReversed: 'Inlet and outlet reversed',
      solutionHeavilyClamped: 'Solution heavily clamped',
      transverseForceAsymmetry: 'Transverse force asymmetry',
    },
    // Numeric §4.2 prefix dropped — Guards is a top-level section now
    // (see `sections.guards`), so the numbering baked into the title
    // would collide with whatever number the ToC assigns.
    title: 'Result checks',
    subtitle: 'Runs that completed but may not be trustworthy',
    flaggedCount: '{{flagged}} of {{total}} flagged',
    allClear: 'No guard signals; every completed run looks physically sound.',
    noRuns: 'No completed runs to check.',
    unphysical: 'Unphysical',
    remediation: 'What to do:',
    codeLabel: 'code:',
    // BE ships un-localised English sentences on `unphysical_reasons`
    // and `sim_error.remediation` today. Until BE emits stable
    // `unphysical_reason_keys`, translateGuardMessage matches known
    // patterns and swaps in these locale strings so a Japanese /
    // Chinese reader does not meet raw English inside a translated UI.
    reasons: {
      wallYPlusMaxAbove:
        'Wall y+ max is {{value}} — above the log-law region ({{threshold}}). Wall shear values are unreliable.',
      wallYPlusMinBelow:
        'Wall y+ min is {{value}} — below the log-law region ({{threshold}}). Wall functions do not hold here.',
      wallYPlusBufferBand:
        '{{value}}% of the wall area sits in the y+ 5–30 buffer band (limit {{limit}}%), where neither the resolved-sublayer nor the wall-function treatment is valid. Wall shear values are unreliable.',
      wallYPlusSublayerLogLaw:
        '{{value}}% of the wall area is below y+ 5 (limit {{limit}}%) on a log-law wall treatment. Wall functions do not hold there.',
      wallYPlusMeanBelowNoFractions:
        'Wall y+ mean is {{value}} — below the log-law region ({{threshold}}) on a log-law wall treatment, and the wall-area fractions are unavailable for this run. Wall functions do not hold here.',
      massImbalance:
        'Inlet/outlet mass imbalance is {{value}}% — the domain is not conserving mass.',
      fluidLimitersClamped:
        'Fluid limiters clamped {{value}}% of cells at the final iteration (over the {{threshold}}% bar). The run converged, but on a field the solver had to hold down — treat the temperatures as indicative until the clamped region is meshed or driven better.',
    },
    remediations: {
      wallYPlusRange:
        'Wall y+ is outside the wall-function valid range. Adjust initial height so y+ lands in 30–100.',
      wallYPlusModelAware:
        "Wall y+ is outside the range this run's wall treatment supports. Adjust initial height: y+ ≈ 1 resolves the viscous sublayer (k-ω SST); 30–100 for wall functions only (k-ε); keep the wall out of the 5–30 buffer band.",
      wallYPlusResolving:
        'With this turbulence model y+ ≈ 1 resolves the viscous sublayer; 30–100 suits wall functions only. Move initial height out of the 5–30 buffer band — smaller for y+ ≈ 1, larger for 30–100.',
      wallYPlusLogLaw:
        'k-ε with log-law wall functions needs the first cell in the log-law region. Raise initial height so y+ lands in 30–100, or resolve the sublayer at y+ ≈ 1 with k-ω SST (or the low-Re epsilon and nut wall functions together).',
      massImbalance:
        'Mass flux imbalance between inlet and outlet is too large. Check the BC set for a missing outlet, or raise non-orthogonal correctors.',
      solutionHeavilyClamped:
        'The fluid limiters clamped a large share of cells, so this answer is indicative rather than wrong — the run converged, but on a field the solver had to hold down. Expect the energy balance to read high: the velocity limiter rewrites the velocity FIELD (U) cell by cell without rewriting the mass flux, so the enthalpy convection term is evaluated against a flux that no longer matches. Coarsen or repair the mesh where the clamped cells are, or lower the inlet velocity, and rerun before trusting the temperatures.',
    },
    // BE-emitted underscore-code fallbacks (e.g. `mass_imbalance_52pct`)
    // — kept separate from `reasons` above (which fill in full BE-emitted
    // sentences) because the input is a code, not a sentence, and only the
    // percentage value survives the parse.
    humanizedCode: {
      massImbalance: 'Mass imbalance {{value}} %',
    },
  },
  cht: {
    interfaceHeatFlux: 'Heat into coolant',
    peakFlux: 'Peak flux',
    converged: 'Converged',
    runtime: 'Runtime',
    energyBalance: 'Energy balance',
    energyBalanceOk: 'closes to rounding',
    //-- Thermal FEA (ENG-2769). NOT the CHT energy-balance wording:
    //-- this row grades whether the mesh resolves the BC-face
    //-- gradient, and a steady conduction solve conserves energy by
    //-- construction, so it must never say the solve lost energy.
    fluxResolutionOk: 'face integrals agree',
    fluxResolutionCoarse:
      'face integrals disagree — mesh too coarse at a BC face',
    fluxResolutionCoarseFace:
      'face integrals disagree — mesh too coarse at {{face}}',
    coarseFaceMark: '· coarse',
    faceHeating: 'Heating',
    faceCooling: 'Cooling',
    faceHeatRateLegendShort: 'Red heating · blue cooling',
    faceHeatRateLegend:
      'Positive watts heat the solid (red); negative watts cool it (blue).',
    imbalanceInflowTooltip:
      'Net inflow — more heat enters the solid ({{in}}) than leaves ({{out}}). Imbalance is |in − out|.',
    imbalanceOutflowTooltip:
      'Net outflow — more heat leaves the solid ({{out}}) than enters ({{in}}). Imbalance is |in − out|.',
    imbalanceBalancedTooltip:
      'Heat in and heat out match within rounding. Imbalance is |in − out|.',
    imbalanceCoarseFace: 'Likely coarse BC face: {{face}}.',
    energyImbalanceTooltip:
      'Energy that did not close: |E_in − E_out − ΔU_stored|. Positive leftover means the bookkeeping did not conserve energy over the period.',
    energyBalanceWarn: 'closes soft — see explanations',
    energyBalanceFail: "did NOT close — solve isn't conserving energy",
    energyBalanceTooltip:
      'Does the heat that went in come back out? Compares declared source vs solved interface flux and reports the imbalance as a percent. Verdict traffic-light: green = OK, amber = soft close with an explanation (a run that did not converge, one the velocity limiter clamped), red = did not close — reader should not trust the temperatures.',
    iterations: '{{count}} iterations',
    belowLimit: '✓ below {{temp}} °C limit',
    aboveLimit: '⚠ above {{temp}} °C limit',
    regions: 'Regions',
    heatSource: 'heat source',
    tMin: 'T min',
    tAvg: 'T avg',
    tMax: 'T max',
    uMax: 'U max',
    chipTmaxTooltip:
      'Highest temperature in any solid region with a heat source. Drives the pass/fail badge against your target temperature.',
    pressureDropTooltip:
      'Pressure loss from inlet to outlet across the fluid region.',
    pressureDropReversed: 'pressure rose; inlet and outlet are likely swapped',
    interfaceHeatFluxTooltip:
      'Net watts the solids give the coolant across every fluid ↔ solid interface in this run. Interfaces on this run: {{patches}}. Positive is the normal cooling direction; negative means the coolant is heating the solids. Per-interface splits appear on the region-graph schematic when the run records them.',
    interfaceHeatFluxIntro:
      'Net watts the solids give the coolant across every fluid ↔ solid interface in this run (negative: the coolant is heating the solids).',
    interfacePatchesLabel: 'Interfaces:',
    interfaceLegend:
      'Arrows point the way heat flows: red = the region giving heat, blue = the region receiving it. Solid → coolant is the normal cooling direction; the reverse usually means a swapped inlet/outlet or a mis-declared source.',
    interfaceHeatFluxTooltipTrans:
      'Net watts the solids give the coolant across every fluid ↔ solid interface in this run. Interfaces: {{patches}}. <heat>Red</heat> = the region giving heat, <cool>blue</cool> = the region receiving it; solid → coolant is the normal cooling direction.',
    peakFluxTooltip:
      'Largest local heat flux anywhere on the fluid ↔ solid interface. Peak on this run lives on: {{patch}}. High values mark hot spots that may need more mesh refinement.',
    peakFluxIntro: 'Largest local flux on the fluid ↔ solid interface.',
    peakPatchLabel: 'Peak lives on:',
    peakFluxOutro: 'High values mark hot spots that may need mesh refinement.',
    peakFluxTooltipTrans:
      'Largest local flux on the fluid ↔ solid interface. Peak lives on <heat>{{patch}}</heat>. High values mark hot spots that may need mesh refinement.',
    patchListUnavailable: 'not reported for this run',
    convergedTooltip:
      "Whether the solver's residuals dropped below the configured tolerance before it ran out of iterations. ✓ means the answer is steady; ○ means it ran the full iteration budget without settling.",
    runtimeTooltip:
      'Wall-clock seconds the solver spent on this design point (mesh time not included).',
  },
  // ENG-1893 — six hand-calc anchors on the CHT result page.
  chtAnchors: {
    subtitle:
      'Plug in the reference values you expected the run to sit at. The run + material libraries seed every field they can, including D_h and L where they were declared on the run; anything left blank is yours to fill in. Reset restores every field to that seed.',
    assumptionsHeading: 'Assumptions',
    derivedHeading: 'Derived',
    resetLabel: 'Reset assumptions to seeded values',
    resetTooltip: 'Reset',
    refPickerLabel: 'Reference values for {{row}}',
    missingInputHint: 'Waiting on {{variable}}',
    refPickerTitle: 'Reference values',
    refPickerHint:
      'Reference formulas — read-only. Pick the geometry that matches your heatsink, apply the equation by hand, and type the result into the row above.',
    refs: {
      genericCoolant: 'this coolant',
      Nu: {
        laminarWallT: 'Laminar channel, T-wall',
        laminarWallQ: 'Laminar channel, q″-wall',
        gnielinski: 'Turbulent (Gnielinski)',
        dittusBoelter: 'Turbulent (Dittus-Boelter)',
        description: {
          laminarWallT:
            'A constant for a round duct in fully developed laminar flow with the wall held at one temperature. Not a correlation — it falls out of the solved velocity and temperature profile, so there is nothing to fit and no scatter band. Use it as a floor: a laminar channel cannot do worse than this.',
          laminarWallQ:
            'The same solved case with a uniform heat flux at the wall instead of a uniform temperature. Higher because the constant-flux profile holds a steeper wall gradient relative to (T_wall − T_bulk); a fixed-temperature wall lets that gradient relax as the fluid warms toward it. Which of the two applies is a question about the boundary condition, not about the flow.',
          gnielinski:
            'The general turbulent correlation, and the one to prefer where it applies: it covers the transition region and a wide Prandtl range, and it carries the friction factor explicitly rather than assuming a smooth-tube one. Its numerator contains (Re − 1000), so below Re ≈ 1000 it returns a negative number — a sign it is outside its band, not a reading.',
          dittusBoelter:
            'The old, simple turbulent fit. Easy to do by hand and still widely quoted, but roughly ±25 % against data and no better than that: it ignores the wall-to-bulk property variation, so it drifts furthest where the temperature difference is largest. That bites on {{fluid}}, whose viscosity swings with temperature — exactly what the (μ/μ_s) term in Sieder-Tate exists to correct and this correlation omits. Prefer Gnielinski where the run is inside its band, and treat agreement between Gnielinski and Dittus-Boelter as reassurance rather than either one as truth.',
        },
        validity: {
          laminar: 'Fully developed laminar — Re < 2300',
          gnielinski: '3 × 10³ < Re < 5 × 10⁶, 0.5 < Pr < 2000',
          dittusBoelter:
            'Re > 10⁴, 0.7 < Pr < 160, and at least ten diameters of run',
        },
      },
      // Fin-EFFICIENCY (η_fin) reference geometries. Row's own ε is the
      // heat-exchanger effectiveness, but readers sizing a heatsink
      // fin stack often reach for this popover expecting fin data —
      // surface the geometries directly rather than send them to Ch. 3.
      eps: {
        rectStraight: 'Straight rectangular fin',
        triangularStraight: 'Straight triangular fin',
        annular: 'Annular fin',
        pinFin: 'Pin fin (cylindrical)',
        arrayOverall: 'Overall array surface efficiency η_o',
        ntuPredicted: 'Predicted from channel flow',
        validity: {
          ntuPredicted:
            'Single stream against a constant wall temperature, and Gnielinski’s own band — 3 × 10³ < Re < 5 × 10⁶, 0.5 < Pr < 2000. Takes nothing from the run’s heat or temperatures, so a gap against the row above is evidence rather than arithmetic.',
        },
      },
    },
    units: {
      massFlow: 'kg/s',
      specificHeat: 'J/(kg·K)',
      // Rendered in the assumption row's unit column when the quantity
      // is dimensionless (Pr today, Nu / Bi / ε if they ever move from
      // derived to assumption). A plain em-dash there reads as "value
      // missing" — the same glyph the value column uses when a row is
      // blocked — so the assumption panel spells the label out.
      dimensionless: "dim'less",
    },
    // Backsolve panel (ENG-1893) — same shape as CFD's C_D-expected /
    // C_L-expected rows. Reader types an expected reference value; the
    // panel inverts the defining equation to a predicted result or a
    // setup input to size.
    backsolve: {
      heading: 'Backsolve',
      subtitle:
        "Type an expected reference value; the panel inverts the defining equation for either a predicted result to pre-register or a geometry/setup input to size. The middle column takes the row's expected value first (the number you'd have pre-registered), then any geometry the wire doesn't carry (D_h, A_in, A_wet) as a smaller inline input beside it.",
      headers: {
        row: 'Row',
        typeIn: 'Inputs',
        typeInHint:
          "Primary input is the expected reference value; smaller inputs beside it are geometry values the run does not carry — D_h (hydraulic diameter), A_in (inlet cross-section), A_wet (interface wetted area). Type them in for the row you're backsolving.",
        backsolve: 'Backsolves',
      },
      rows: {
        R: {
          label: 'R expected → predicted T_source',
          hint: "Type the resistance you expect; get the temperature the run should land on. T_source = T_in + Q̇·R_exp — pre-registered, then compare against the run's hot-spot temperature.",
          output: 'T_source',
        },
        rthQMax: {
          label: 'Target T + R_th → package power budget Q̇_max',
          hint: "Complementary inversion to the R-expected row. Type the temperature the part must stay under and (in the R_exp aux beside it) the thermal resistance you expect. Get Q̇_max = (T_target − T_in) / R_exp — the biggest heat load the run's plumbing can carry before the junction crosses its limit. Sizes the power budget for a package selection, or a de-rating check when the source region is not electronics (heater element, motor coil, LED). Blocks until BOTH T_target and R_exp are typed and T_target > T_in.",
          qMax: 'Q̇_max',
        },
        dtBudget: {
          label: 'ΔT budget → required ṁ + U_inlet',
          hint: 'Type the coolant rise you can allow; get the flow the pump must deliver. ṁ = Q̇ / (c_p·ΔT); U = ṁ / (ρ·A_in). Steady-flow-energy inversion — the primary pump-sizing line.',
          mDot: 'ṁ',
          uInlet: 'U_inlet',
        },
        epsExpected: {
          ntuTip:
            "NTU (Number of Transfer Units) = U·A / C_min — the exchanger's dimensionless heat-transfer size. Roughly, how many times the coolant can be heated through the mean ΔT driving it. NTU 1 / 2 / 3 → ε 0.63 / 0.86 / 0.95 for single-stream / constant wall T.",
          label: 'ε expected → required NTU + U·A_wet',
          hint: "Heat-exchanger ε (Kays & London), NOT fin effectiveness. YOU TYPE ε — the target effectiveness (fraction of the ideal ΔT the cooler recovers). NTU and U·A_wet on the right are what this row COMPUTES from it. Each step toward ε = 1.0 costs much more area. NTU (Number of Transfer Units) = U·A / C_min is the exchanger's dimensionless heat-transfer size — how many times the coolant can be heated through the mean ΔT driving it. NTU = −ln(1 − ε) is valid for single-stream / constant wall T; U·A_wet = NTU·ṁ·c_p. NTU 1 / 2 / 3 → ε 0.63 / 0.86 / 0.95. Counter-flow, parallel-flow, cross-flow and mixed arrangements each have their own ε-NTU curve — read those from Kays & London. For fin surfaces use fin efficiency η_fin from Incropera Ch. 3 instead.",
          // A symbol, so the same in every locale — but a key rather than a
          // literal, because every other output label on these rows is one and
          // the renderer translates them all the same way.
          ntu: 'NTU',
          uaWet: 'U·A_wet',
          arrangement: 'Arrangement',
          arrangementValue: 'Single-stream / constant wall T',
        },
        piBudget: {
          label: 'Π budget → max Δp',
          hint: 'Π = Δp / (ρ·c_p·ΔT) — your pumping-cost budget as a FRACTION, so type 0.01 or 1% (both store the same value). Returns the biggest pressure drop the design may have, the pump-side ceiling: Δp_max = Π·ρ·c_p·ΔT (V̇ cancels). 1 % with EGW at ΔT = 5 K allows ≈ 1.8 × 10⁵ Pa.',
          dP: 'Δp max',
        },
        reTarget: {
          label: 'Re target → required U_inlet',
          hint: "Type the Reynolds number a reference row needs; get the inlet speed that produces it. U = Re·ν / D_h. Places the run inside a correlation's validity window before solving.",
          uInlet: 'U_inlet',
        },
        nuExpected: {
          label: 'Nu expected → predicted wall ΔT',
          hint: "Type the published Nu for this channel; get how many degrees the wall sits ABOVE the coolant. ΔT_wall = T_wall − T_coolant — a plain ΔT (also called wall superheat in some texts). Formula: h = Nu·k_fluid / D_h; ΔT_wall = q″ / h with q″ ≈ Q̇_in / A_wet. Pre-registers wall ΔT against the run's peak flux.",
          h: 'h',
          dtWall: 'ΔT_wall',
        },
        targetT: {
          label: 'Target T_max + Q̇ → required Nu (feasibility)',
          hint: 'Type the temperature the part must stay under; see whether any mesh can reach it. h_req = Q̇ / (A_wet·ΔT); Nu_req = h_req·D_h / k. Nu_req above the correlation at reachable Re reclassifies the case — Define re-entry, a finer mesh changes nothing.',
          hReq: 'h_req',
          nuReq: 'Nu_req',
        },
        biLimit: {
          label: 'Bi max → largest lumped solid L_max',
          hint: 'How thick can the solid get before you can no longer treat it as one uniform temperature? Type your Biot ceiling — 0.1 is the textbook one (Incropera Ch. 5). Bi_max = the LARGEST Biot number you still accept as "lumped" (small Bi = interior-uniform; the ceiling is a max, not a min). Get L_max, the thickest slab that still stays lumped. Anything thicker develops meaningful interior ΔT, and the surface temperature stops representing the hottest point inside. Formula: L_max = Bi_max·k_solid / h; flip it to k_req = h·L / Bi_max if the thickness is fixed and you\'re shopping for a higher-conductivity material. h comes from the Nu-chain row above or the target-T feasibility row — the output row names which one. Fill one of those first if h reads "unavailable".',
          lMax: 'L_max',
          h: 'h',
          hSource: 'h from',
          hSourceNu: 'Nu-expected row',
          hSourceTargetT: 'target-T feasibility row',
          hSourceNone: 'unavailable — fill Nu-expected or target-T first',
          hSourceNuShort: 'from Nu',
          hSourceTargetTShort: 'from target-T',
        },
        rePlusPr: {
          label: 'Re + Pr → minimum developed length',
          hint: 'The channel length the flow needs before textbook values apply; shorter channels transfer more than the tables say. Laminar (Re < 2300): L_t ≈ 0.05·Re·Pr·D_h; L_h ≈ 0.05·Re·D_h. Turbulent: L_t ≈ L_h ≈ 10·D_h. Below these, developing-flow rows apply and the fully-developed Nu is the floor.',
          lT: 'L_t (thermal)',
          lH: 'L_h (hydrodynamic)',
        },
        interfaceScale: {
          label: 'Interface flux pair → A_wet scale check',
          hint: "Auto — no input. A_wet ≥ Q̇_iface / q″_peak. When this lower bound disagrees with the CAD by a factor of ~100 or more, a unit / scale error is likely (the CHT twin of CFD's length-scale check).",
          aWetMin: 'Minimum wetted area',
        },
      },
    },
    // Multi-DP radar of the six anchors (ENG-1893). Analog of CFD
    // Section4RadarChart: one polygon per completed DP, each axis a
    // logarithmic distance from the anchor's per-anchor target.
    radar: {
      heading: 'Self-consistency radar',
      subtitle:
        "L1 in the V&V ladder for CHT — arithmetic agreement between the run and the solver only. The radar reads the RUN's wire data (declared BC, solved fields) and does NOT pick up the values you type into the Hand-Calc panel above; those are for the reader's own scratch pad. Axes: Interface power / Coolant power (declared vs solved Q̇ balances), Effectiveness (heat-exchanger ε vs the target the setup declared), Thermal resistance (declared R vs solved source-solid-to-coolant R). Nusselt / Solid isothermality / Colburn drop on when the wire carries an h estimate the run does not yet emit. Identity axes score against a 1 % band; correlation axes against 30 %. Score is worst-axis only. A spoke may drop out of the polygon when its number came out non-physical (a reversed inlet/outlet, a null upstream); the axis stays listed below so the reader still sees which anchors are pending. A run can pass every arithmetic identity here and still be non-physical, so read this as a self-test only — L2 (surrogate / DOE), L3 (verification), L4/L5 (measured; bench / instrumented) drop on as new layers when their imports land.",
      empty: 'No completed CHT design points to score yet.',
      noScoreable:
        'This run has no declared heat source and no coolant-side data — every axis is blocked, so the radar has nothing to draw. Declare a total-power heat source, or wait for a run that emits the coolant fields.',
      identityBand: 'identity · 1% band',
      correlationBand: 'correlation · 30% band',
      pending: 'waiting on run interface data',
      distributionHeading: 'Scored-axis distribution',
      worst: 'Worst',
      median: 'Median',
      best: 'Best',
      symbol: 'Symbol',
      hand: 'Hand',
      solver: 'Solver',
      deltaPct: 'Δ %',
      score: 'Score',
      placeholderTooltip:
        'This axis is a placeholder — the solver does not emit both sides yet.',
      kinds: {
        identity: 'identity',
        correlation: 'correlation',
        placeholder: 'placeholder',
      },
      axes: {
        qInterface: 'Interface power',
        qCoolant: 'Coolant power',
        qCoolantInterface: 'Coolant ↔ interface',
        qV: 'Volumetric heat density',
        epsilon: 'Effectiveness',
        R: 'Thermal resistance',
        Bi: 'Solid isothermality',
        Nu: 'Nusselt number',
        j: 'Colburn j-factor',
      },
      tooltips: {
        qInterface:
          'Declared heat-source total (motor coil, heater element, LED, chip die — whatever the setup named) vs the heat flux the solver reports across the fluid–solid interface. Identity — must close to rounding on a converged run.',
        qCoolant:
          'Declared heat-source total (motor coil, heater element, LED, chip die — whatever the setup named) vs the coolant enthalpy rise (ṁ · c_p · ΔT). Identity — requires the coolant-side aggregates from newer solver runs.',
        qCoolantInterface:
          "Two SOLVER-side energy totals cross-checked against each other: the flux crossing the fluid–solid interface vs the enthalpy the coolant carries out (ṁ · c_p · ΔT). Independent of what the setup declared — if these disagree, the SOLVE isn't conserving energy (a partial balance, a leaked patch, a wall the interface totals didn't sum). Identity — must close to rounding.",
        qV: 'Volumetric heat source density q̇_v = Q̇/V_source, declared side vs measured side (Q̇_interface/V_source). Identity — closure means every watt put into the source solid exits across the fluid interface. A mismatch names an energy path the interface totals did not see (an ambient BC on the solid, another solid–solid interface). Blocked on older runs whose source solid does not report its meshed volume.',
        epsilon:
          'ε = Q̇ / (ṁ · c_p · ΔT_max). Correlation-band score against a typical ε-NTU sweet spot of ~0.7.',
        R: 'R = (T_source − T_in) / Q̇. Correlation-band score against a typical liquid-cold-plate target of ~1 K/W.',
        Bi: 'Bi = h · L / k_solid. Scoring waits on an h estimate the run does not yet emit (wetted interface area + wall–bulk ΔT).',
        Nu: 'Nu = h · D_h / k_fluid. Scoring waits on an h estimate the run does not yet emit (wetted interface area + wall–bulk ΔT).',
        j: 'j = St · Pr^(2/3). Scoring waits on the same h estimate as Nu / Bi.',
      },
    },
    // Sub-panel below the anchors — energy balance identities.
    // Same shape as CFD's self-consistency table (arithmetic-agreement
    // Δ where a mismatch is a plumbing bug, not a validation signal).
    chtChecks: {
      heading: 'Consistency check',
      subtitle:
        'Arithmetic identities that must match to rounding on a clean run. A Δ here is a plumbing bug (a swap between labels, a null the reader read as zero, a partial energy balance), not a validation signal.',
      headers: {
        identity: 'Identity',
        hand: 'Hand',
        solver: 'Solver',
        absDelta: '|Δ|',
        pctDelta: 'Δ %',
      },
      rows: {
        qInterface: {
          label: 'Power in ↔ Power across fluid–solid interface',
          hint: "Sum of the declared heat sources on this run's solid regions against the interface heat flux the solver reports. Should close to rounding on a converged run — a big Δ means the solver did not deliver every declared watt through the fluid–solid interface.",
        },
        qCoolant: {
          label: 'Enthalpy conservation · Power in ↔ ṁ·c_p·ΔT',
          hint: "Enthalpy conservation check: the coolant enthalpy rise (ṁ · c_p · ΔT) must match the total heat source declared on the run's solid regions. A big Δ here means the coolant is carrying away more or less power than the sources deliver — a leak through an ambient BC, a wrong sign, or an inlet/outlet swap. Requires the coolant-side aggregates from newer solver runs.",
        },
        qBothSides: {
          label: '|Q̇_fluid| ↔ |Q̇_solid| (interface closure)',
          hint: 'Purely solver-side check — the fluid side of the interface must see the same watts the solid side does, sign-flipped. Compares the magnitudes so the reader ignores the sign convention. Independent of what the setup declared: a mismatch means the interface coupling itself did not close (a patch pair the solver did not sum, a mesh-non-orthogonality-driven flux imbalance).',
        },
        qCoolantInterface: {
          label: 'Energy balance · Q̇_interface ↔ ṁ·c_p·ΔT',
          hint: 'Two SOLVER-side energy totals cross-checked against each other: the flux crossing the fluid–solid interface vs the enthalpy the coolant carries out. Independent of what the setup declared — a mismatch names the SOLVE as not conserving energy (a partial balance, a wall the interface totals did not sum). qInterface + qCoolant can each close against the declaration while these two disagree; this row catches that case.',
        },
        qV: {
          label: 'Volumetric heat density · Q̇/V_source ↔ Q̇_iface/V_source',
          hint: 'Declared volumetric heat density (Q̇/V_source) vs measured (Q̇_interface/V_source). Closure means every watt put into the source solid exits across the fluid interface. A mismatch names an energy path the interface totals did not see (an ambient BC on the solid, another solid–solid interface). Requires the source solid to have a reported meshed volume.',
        },
        massImbalance: {
          label: 'ṁ inlet ↔ ṁ outlet (mass balance)',
          hint: 'Solver-reported coolant mass imbalance should be ~0. A non-zero imbalance means the solver did not close the mass balance to rounding.',
        },
      },
      pending: {
        qDeclared:
          'No declared heat source to compare against — declare one, or type the power manually.',
        interface: 'In development.',
        coolant: 'In development.',
      },
    },
    tooltips: {
      dh: 'Hydraulic diameter of the flow passage. Seeded from the value declared on the run when there is one; otherwise type the geometry value or a quick estimate (4·A/P for the wetted cross-section). Never solved for — it is a declared number either way.',
      L: 'Characteristic length for solid conduction — usually the thickness heat travels through. Seeded from the value declared on the run when there is one; otherwise type it in. Never solved for.',
      qDot: "Sum of declared heat sources on the run's solid regions when total power was declared. Auto when set; manual when the setup declared no source. Setups that declared a W/m³ density need the body volume to convert, which the run does not carry.",
      tSource:
        "Hottest solid temperature — the run's per-region max solid T, or the heat-source solid max when the setup names one.",
      tIn: 'Fluid inlet temperature from the boundary condition submitted with the run.',
      dP: 'Pressure drop across the fluid region.',
      mDot: 'Coolant mass flow — auto on newer runs; blank on older runs where the solver did not aggregate it. Type it in there.',
      dT: 'Coolant enthalpy rise across the fluid region. Same wire status as ṁ.',
      kSolid:
        "Solid thermal conductivity from the material library, keyed by the first solid region's material. Multi-solid scenes still show one value; per-region k for Bi is a follow-up.",
      kFluid:
        "Fluid thermal conductivity, read straight off the library entry for the run's declared coolant.",
      rho: "Fluid density from the library entry for the run's declared coolant.",
      cp: "Fluid specific heat from the library entry for the run's declared coolant.",
      prandtl:
        'Fluid Prandtl number, derived from the library entry as Pr = ρ·ν·c_p/k — the same derivation the solver is handed.',
      vSolid:
        'Meshed volume of the source solid — the region carrying the declared heat source. Feeds q̇_v = Q̇ / V_solid; used to size the junction density against a package rating.',
      aWet: 'Total wetted interface area, summed across every fluid ↔ solid patch pair the run recorded. Feeds h_eff = Q̇ / (A_wet · ΔT). Blank on older runs — h_eff then falls back to its L² approximation. Reader can override to test a design change without a re-run.',
    },
    state: {
      needsCoolant: "Waiting on the solver's coolant aggregates (ṁ + ΔT).",
      needsDh: 'Waiting on hydraulic diameter D_h — type it above.',
      needsBoth:
        "Waiting on the solver's coolant aggregates and hydraulic diameter D_h.",
      missingInputs: 'Missing inputs: {{missing}}',
    },
    rows: {
      R: {
        label: 'Thermal resistance R',
        reference:
          'How many degrees the part heats up per watt it must shed. Lower is better. Liquid cold plates land between 0.1 and 10 K/W — a miss splits into conduction / wall-to-coolant / coolant-warming to place where it went.',
        source:
          "Thermal Ohm's law · design guideline for single-phase liquid cold plates",
      },
      Bi: {
        label: 'Solid isothermality (Bi)',
        observed: 'Δ{{region}} = {{dt}} K',
        reference:
          'Checks whether a solid part sits at one temperature. Metals usually do; a big internal spread points at part contact or a material value in the setup. Bi < 0.1 = lumped; direction-only, so a spread above the Bi prediction implicates interface coupling or the conductivity entry.',
        source: 'Lumped-capacitance criterion · Incropera Ch. 5',
      },
      Nu: {
        label: 'Nusselt number Nu',
        reference:
          'Scores how much better the flowing coolant moves heat than still fluid would. Judged against a published value for this channel type: Shah & London 3.66 / 4.36 exact laminar (wall BC moves it 19 %), Gnielinski ±10 % (3 × 10³ < Re < 5 × 10⁶), Dittus-Boelter ±25 % fallback.',
        source: 'Gnielinski; Dittus-Boelter; Shah & London · Incropera Ch. 8',
      },
      Pi: {
        label: 'Pumping cost Π',
        reference:
          'Watts spent pumping coolant per watt of heat removed. A healthy liquid loop spends well under 1 %. A rising Π flags channel hydraulics before temperatures move.',
        source:
          'Π = V̇·Δp / (ṁ·c_p·ΔT) = Δp / (ρ·c_p·ΔT) · design guideline for liquid loops',
      },
      j: {
        label: 'Colburn factor j',
        reference:
          'Cross-checks the run against itself: the heat answer and the pressure-drop answer come from the same wall physics and must agree. j ≈ f / 8 within ±20 % while wall shear dominates Δp; pin fins break the f shortcut, measured j(Re) stays valid.',
        source: 'Chilton-Colburn analogy · Incropera §6.8',
      },
      eps: {
        label: 'Effectiveness ε_HX',
        reference:
          'Heat-exchanger effectiveness — how close this cooler gets to the best any cooler of this layout could do. 1.0 is the physical limit. ε = Q̇_in / (C_min · ΔT_max), C_min = ṁ·c_p. Judge against the ε-NTU curve for the arrangement (Kays & London, ±10 %); single-stream / constant wall T gives ε = 1 − e^(−NTU). NOT to be confused with fin effectiveness ε_fin = q_fin / (h·A_b·(T_b − T_∞)) or fin efficiency η_fin = q_fin / q_ideal — different quantities per Incropera Ch. 3 (tables for straight rectangular / triangular / annular / pin fins).',
        source: 'Kays & London · Compact Heat Exchangers',
      },
      qV: {
        label: 'Volumetric heat source density q̇_v',
        reference:
          'Watts the source solid dissipates per cubic metre of its body — an electronics-package junction-density spec (though the source could equally be a heater element, coil, or LED). Q̇ / V_solid; V_solid is the meshed volume of the region carrying the heat source. Typical CPU dies land near 10⁸ W/m³ (100 MW/m³); a rating well above that means the source solid is being asked to dump more power than its area can shed and the solve will find a hot part regardless of what the coolant does.',
        source: 'Junction-density spec · package-rating comparison',
      },
      Rcond: {
        label: 'Conduction resistance R_cond',
        reference:
          "Fourier resistance of a solid path: L / (k_solid · A). The relation that applies where no fluid touches the surface — a solid-solid contact has no film coefficient, so Nu, the Colburn j and the first-cell height are all undefined there. Compare it against the run's own R = ΔT / Q̇ on the same scope, in the same K/W: agreement means the contact behaves like bulk conduction through the thickness you assumed, and a hand value well BELOW the solver's says the real contact adds resistance the ideal path does not (an interstitial gap, thermal paste, an imperfect bond).",
        source: 'Fourier conduction · one-dimensional slab',
      },
      hEff: {
        label: 'Effective interface HTC h_eff',
        reference:
          "Order-of-magnitude convection coefficient the wall sheds heat with. Q̇ / (L²·ΔT_wall→bulk) — the same h that Bi / Nu / j already use internally, surfaced so the reader can pair it against the convection BC's declared h. Uses L² as an A_wet stand-in until the mesh emits wetted area. Water in a channel typically 10³–10⁴ W/(m²·K); forced air 50–500; boiling 10⁴–10⁵.",
        source: 'Newton cooling · h = q″ / ΔT · Incropera Ch. 6',
      },
    },
  },
  fea: {
    fixtureLoadFaces: 'Fixture and load faces',
    hideFace: 'Hide {{face}}',
    showFace: 'Show {{face}}',
    field: 'Field',
    aboutField: 'About this field',
    slice: 'Slice',
    slicePosition: 'Slice position',
    sliceInBody: '{{position}} mm · in {{label}} ({{volume}})',
    sliceInBodyUnlabeled: '{{position}} mm · in {{volume}}',
    viewAlongSlice: 'View {{axis}}',
    bodies: 'Bodies',
    coloredFaces: 'Colored faces',
    otherFaces: 'Faces',
    fixturesAndLoads: 'Fixtures and loads',
    thermalBoundaryConditions: 'Thermal boundary conditions',
    contactsAndPreloads: 'Contacts and preloads',
    contactsBondsAndPreloads: 'Contacts, bonds and preloads',
    interfacesAndTies: 'Interfaces and ties',
    // Setup list aria / missing-value copy (ENG-3002).
    highlightTarget: 'Highlight {{detail}}: {{label}}',
    detailsAndHighlightTarget: 'Details and highlight {{detail}}: {{label}}',
    notReported: 'Not reported',
    sharedMeshInterfaces: 'Shared mesh joins',
    sharedMeshInterfaceHint:
      'Body pairs joined by common mesh faces or edges. Edge-only joins have no heat-conducting area. Remesh older meshes for measured geometry and highlighting.',
    edgeOnlyJoin: 'Edge only',
    pointOnlyJoin: 'Point only',
    areaUnavailable: 'Remesh for area',
    bodyUnassigned: 'Unassigned',
    deformedShape: 'Deformed shape',
    scale: 'Deformation scale',
    scaleAuto: 'Auto ({{factor}}×)',
    scaleFactor: '{{factor}}× true scale',
    reactionsTitle: 'Fixture reactions',
    reactionsHint: 'what each support carries — a result, not an input',
    reactionFixture: 'Fixture',
    reactionTotal: 'Total (N)',
    reactionAxial: 'Axial (N)',
    reactionShear: 'Shear (N)',
    reactionTension: 'tension',
    reactionBearing: 'bearing',
    reactionNodes: '{{count}} nodes',
    reactionNoNormal: 'no single face normal — total only',
    peak: 'Peak {{value}} {{unit}}',
    mode: 'Mode',
    modeOption: 'Mode {{mode}} · {{frequency}}',
    modeNa: 'N/A',
    frequencyHz: '{{frequency}} Hz',
    failedVisualization: 'Failed to load visualization',
    legacyArtifact:
      'Rerun required for line probes and per-body controls. This result predates volume connectivity.',
    lineProbeUnavailable:
      'This FEA result contains a surface VTU, so you can place and inspect the probe line on the model, but it has no volume tree for field interpolation. Rerun with volumetric result artifacts to plot samples.',
    scrubber: {
      label: 'Frame',
      play: 'Play',
      pause: 'Pause',
      slider: 'Result frame',
      value: '{{value}} {{unit}}',
      frames: 'frames ({{count}})',
      framesHelp:
        '{{count}} published result frames along this axis. Play advances one frame every quarter-second and wraps at the ends. Scrubbing loads the matching field shard without remounting the mesh.',
      unit: {
        time_s: 's',
        frequency_Hz: 'Hz',
        load_factor: '×',
      },
    },
  },
  // ENG-1545 Block G; references + traceability appendix.
  // ENG-1899 — figure chrome. The backend sends what the data IS; every string
  // a reader sees on a chart is here, in three languages.
  figures: {
    addChartToWriteup: 'Add “{{alt}}” to the writeup',
    axis: {
      distance: 'Distance along line (mm)',
      term: 'Term',
      flowRate: 'Flow rate',
      check: 'Check',
      input: 'Input',
      iteration: 'Iteration',
      residual: 'Residual',
      score: 'Score',
      value: 'Value',
      varianceShare: 'Share of variance',
    },
    boundsFitted: 'Axis bounds fitted to the data shown.',
    unmeasured_one:
      '{{count}} point unmeasured and drawn as a gap, not a zero.',
    unmeasured_other:
      '{{count}} points unmeasured and drawn as gaps, not zeros.',
  },
  appendix: {
    references: {
      heading: 'References cited',
      empty:
        'No pre-registered references apply to this study template. Add one from the Reference comparison picker in the Results section.',
    },
    traceability: {
      heading: 'Data traceability',
      studyId: 'Study ID',
      generatedAt: 'Report generated',
      designPoints: 'Design points',
      dpCount: '{{completed}} completed of {{total}}',
      geometryIds_zero: 'Geometry IDs',
      geometryIds_one: 'Geometry ID',
      geometryIds_other: 'Geometry IDs ({{count}})',
      meshIds_zero: 'Mesh IDs',
      meshIds_one: 'Mesh ID',
      meshIds_other: 'Mesh IDs ({{count}})',
      simulationIds_zero: 'Simulation IDs',
      simulationIds_one: 'Simulation ID',
      simulationIds_other: 'Simulation IDs ({{count}})',
      attemptTag: '(attempt {{attempt}})',
    },
  },

  /** The Analyze Overview's own tiles — verdict, setup line, scorecard,
   *  anchors, response, run health, design points — and its jump strip.
   *  Section bodies the dashboard shares with the document keep their
   *  keys above. */
  overview: {
    help: {
      completion:
        'Confirms that the solver finished every requested load step. Completion alone does not establish strength, stability or force balance.',
      contact:
        'Uses measured opening of declared contact pairs. Missing contact measurements are not evidence that the joint stayed closed.',
      forceBalance:
        'Compares final applied forces with support reactions in global X, Y and Z, counting shared restrained node/DOFs once. Pass requires complete load coverage and imbalance within the recorded tolerance. Older runs need a rerun on the updated backend or reprocessing of saved outputs.',
      moment:
        'Compares applied and support-reaction moments about the mass-weighted assembly center of gravity. This is a global equilibrium resultant, not the maximum internal bending moment at a section.',
      mesh: 'Requires at least three comparable mesh resolutions and the same physical response. A single completed mesh cannot establish mesh independence.',
      anchor:
        'Compares an independent hand calculation with the same design point and current simulation attempt. Generate a calculation and supply its missing assumptions; missing comparisons are not passing checks.',
      residuals:
        'Checks recorded solver residuals against the applicable convergence criteria. A completed process or a low value for one equation alone is not proof of convergence.',
      guard:
        'Uses the recorded physics guard checks for this solver and setup. Open the affected design point to inspect any flagged condition.',
      mass: 'Compares integrated inlet and outlet mass flow. The displayed percentage is the recorded relative imbalance; missing flow measurements remain unassessed.',
      bounding:
        'The solver adjusted a computed value to its numerical limits. This is separate from convergence and material strength. Missing records do not prove no adjustment occurred.',
      yPlus:
        'Checks near-wall mesh resolution against the active wall-treatment range. This applies to flow models, not structural FEA.',
      modes:
        'Shows recorded natural frequencies and how many requested modes were extracted. Each mode has its own frequency; the lowest frequency is not the whole spectrum.',
      nonPositive:
        'Counts recorded non-positive eigenvalues or frequencies. This is not a mode numbered zero and does not detect every small positive rigid-body-like mode.',
      heat: 'Compares heat entering and leaving in the final-step boundary-face integrals. The band is max(1% of the larger input/output magnitude, 0.000001 W). These integrals use the recovered element temperature gradient, so a wide spread flags boundary-gradient mesh resolution; it is advisory, not an energy-conservation or solver-convergence failure. Per-face nodal reactions may differ from these integrals.',
      energyConserved:
        'Compares energy into and out of the solid over the period (∫Q·dt) with the change in stored thermal energy. A fail means the books did not close — not the same question as the final-step face-integral mesh check above.',
      temperatureBounds:
        'Maximum-principle check: every node temperature must stay between the coldest and hottest fixed temperatures (and ambient sinks) applied on the run. A fail means a node left that physical envelope.',
      stress:
        'Peak nodal-extrapolated von Mises stress in MPa. It is an equivalent stress, not ultimate strength. Peaks can depend on mesh refinement and local singularities.',
      displacement:
        'Largest recorded nodal displacement magnitude in mm. Compare it with a physical displacement limit and the small-displacement assumption; completion alone does not make a very large displacement acceptable.',
      safety:
        'Yield strength divided by the reported peak von Mises stress. Below 1 fails; 1 to below 1.5 needs attention; 1.5 or above passes this margin check only. Assembly headlines can use the weakest assigned material; inspect per-body results.',
      peak: 'Original mesh node ID at the peak nodal-extrapolated von Mises stress. It is not an element ID or an integration-point stress peak.',
      result:
        'Recorded solver output for the selected design point, in the displayed units. Missing values are not zero, and a recorded value is not itself a verification pass.',
      reaction:
        'Support force resolved in global axes. Only restrained directions are reported. Shared nodes can occur in several fixture rows, so their magnitudes must not be summed as global equilibrium.',
    },
    sweep: {
      thresholds: 'Yield safety thresholds: 1 and 1.5',
      summary: 'Across all design points',
      completedOnly:
        'Distributions use recorded values from completed runs. Missing and failed runs remain in the coverage count.',
      coverage: '{{n}}/{{total}} DPs recorded',
      distribution: 'Design-point value distribution',
      designPoint: 'Design point {{label}}',
      minimum: 'Minimum',
      median: 'Median',
      maximum: 'Maximum',
      pairedHelp:
        'Each pair compares an independent calculation with the same design point’s solver result. Different relations, scopes and units stay separate.',
      noMatches:
        '0/{{total}} DPs have matched comparisons. Open a design point below to review or generate its calculation.',
      matched: '{{n}}/{{total}} DPs matched',
      legend: '○ Expected · ◆ Solved',
      pairValues: 'Expected {{expected}} {{unit}}; solved {{solved}} {{unit}}',
      errors:
        'Absolute difference: median {{median}}%, worst {{worst}}% ({{n}} reported)',
      inspect: 'Inspect or generate a design-point comparison',
      selectedEvidence: 'Selected design point — balances, reactions and modes',
      checkCoverage:
        '{{passed}} passed · {{flagged}} flagged · {{missing}} not assessed',
    },
    evidence: {
      waitingForRun: 'Comparisons become available when this run completes.',
      comparisonsReady:
        '{{n}} / {{total}} comparisons ready. Accepted calculation edits update this panel.',
      noPrimaryComparison:
        'The hand calculation is available, but no primary comparison is bound to these overview results.',
      comparisonsNeedInputs:
        'Calculation finished; no matched comparison is ready. Review the assumptions and missing inputs in the design point.',
      forceBalanceValues: 'Imbalance {{error}} · tolerance {{tolerance}}',
      momentBalanceTitle: 'Moment balance about CG',
      momentBalanceValues: 'Imbalance {{error}} · tolerance {{tolerance}}',
      appliedMoment: 'Applied',
      reactionMoment: 'Support reaction',
      cgReference: 'CG (m): {{xyz}}',
      fixtureMoment: 'Moment about CG: {{value}}',
      peakStressNode: 'Peak von Mises node',
      peakDisplacementNode: 'Max displacement node',
      expectedTitle: 'Expected vs solved',
      expected: 'Expected',
      expectedMissing:
        'No independent expectation is available yet. Recorded solver values are shown; a dash is not a failed check.',
      safetyBands:
        'Yield safety factor: below 1 fails; 1 to below 1.5 needs attention; 1.5 or above passes the yield-margin check. This does not validate the whole model.',
      generate: 'Generate comparisons',
      flaggedCheck:
        '{{check}} needs attention. Review the recorded evidence for the affected design points.',
      recorded: 'Recorded results for the selected design point',
      contactSeparation: 'Contact separation',
      modesExtracted: 'Requested modes extracted',
      nonPositiveModes: 'Non-positive modes detected',
      bucklingLoadMargin: 'Buckling factor above applied load',
      heatBalance: 'Boundary flux resolution',
      heatBalanceValues:
        'Face integrals differ by {{error}} · band {{tolerance}} ({{heatIn}} in · {{heatOut}} out)',
      temperatureBounds: 'Temperature bounds',
      surfaceFluxes: 'Boundary face heat rates',
      spectrum: 'All extracted modes',
      mode: 'Mode {{n}}',
      modeHelp:
        'The count identifies non-positive eigenvalues or frequencies, not mode zero. Small positive near-zero modes are not classified by this check.',
      openCalculations: 'Open design point',
      handCalc: 'Hand Calc',
      calculationUnavailable:
        'Calculations could not be loaded. Recorded solver results remain available above.',
      noComparisons:
        'No independent calculation is loaded for this simulation attempt in this browser session. Generate comparisons or open the design point to supply missing assumptions. Solver results alone cannot fill Expected.',
      retry: 'Retry',
      showLess: 'Show fewer comparisons',
      showAll: 'Show all {{count}} comparisons',
      assumptions: 'Assumptions and references',
      checkSummary:
        'Checks: {{passed}} passed · {{flagged}} need attention · {{missing}} not assessed',
      correctionsHelp:
        'OpenFOAM reported adjusting a computed quantity to its allowed limits, such as a negative turbulence value. This is a numerical safeguard, not an engineering limit check. This is separate from convergence: a run can converge after these corrections. Missing records are not proof that no adjustment occurred.',
      meshRequired: 'Requires three comparable mesh resolutions',
    },
    jump: {
      regionLabel: 'Jump to a section of the overview',
      verdict: 'Verdict',
      trust: 'Trust',
      checks: 'Checks',
      anchors: 'Results & balances',
      meshIndependence: 'Mesh ind.',
      results: 'Results',
      runHealth: 'Run health',
      response: 'Comparisons',
      sweepResponse: 'Sweep response',
      points: 'Design points',
      evidence: 'Evidence',
      fields: 'Fields',
      pareto: 'Pareto',
      sensitivity: 'Sensitivity',
      surrogate: 'Surrogate',
      context: 'Context',
      setup: 'Setup',
      appendix: 'Appendix',
      comments: 'Comments',
    },
    verdict: {
      eyebrow: 'Verdict',
      kind: {
        trustworthy: 'Trustworthy',
        directional: 'Directional only',
        unusable: 'No usable result',
        nothingYet: 'Nothing to report yet',
      },
      headline: {
        unusable:
          'No usable result — {{failed}} of {{total}} runs did not complete',
        trustworthy:
          'All {{ran}} checks pass across {{completed}} of {{total}} runs',
        nothingYet_one: 'No run has produced a result yet',
        nothingYet_other:
          'None of the {{count}} runs has produced a result yet',
        flagged:
          '{{flagged}} of {{completed}} completed runs flagged for {{check}}',
        qualified: 'Directional until {{check}} is settled',
        unexamined: 'No checks ran on {{completed}} completed runs',
      },
      why: {
        unusable: 'Nothing completed, so there is no response to read.',
        unusableReason: 'Most common failure: {{reason}}',
        nothingYet:
          'Nothing has finished and nothing has failed, so there is nothing to judge yet.',
        trustworthy:
          'Every check that ran passed. The scorecard lists what did not run.',
        unexamined:
          'The runs completed but reported none of the signals the checks read. Treat the numbers as unverified.',
        residuals:
          'A run that did not converge reports a snapshot, not a solution. Treat its numbers as one sample of a distribution rather than the answer.',
        guard:
          'The solver finished but the physics does not close. Until the cause is ruled out, treat the result as directional.',
        massBalance:
          'Mass leaving the domain does not match mass entering it. The pressure drop and flow rates depend on where it went.',
        energyBalance:
          'The run cannot account for the power it was given. A steady solve closes its energy books when it is done and not before, so the temperatures are a snapshot of a solve still in motion.',
        bounding:
          'The solver adjusted computed values to its allowed limits. Review where and how often these corrections occurred before relying on the result.',
        anchor:
          'The solver disagrees with the hand calculation by more than the anchor tolerates. One of them is wrong about the setup.',
        yPlus:
          'The wall-model range was not met, so the near-wall forces are outside what the turbulence model is calibrated for.',
        meshIndependence:
          'The result still moves with the mesh. Refine once more before quoting it.',
      },
      actions: {
        reviewFlagged: 'Review flagged runs',
        seeFailures: 'See the failure group',
        openWriteup: 'Open writeup',
        optimizeInCadMorph: 'Optimize in CadMorph',
        optimizeInCadMorphHint:
          'Opens the best run’s geometry with this study’s objectives, parameters and drivers attached.',
      },
    },
    kpi: {
      best: 'best',
      bestNoteSpread: '{{dp}} · spread ±{{spread}}',
      noValue: '—',
      vsHandCalc: 'vs hand calc',
      completed: 'Completed',
      runtime: 'Runtime',
      runtimeMedian: '{{median}} median',
      stripLabel: 'Run outcomes, in sweep order',
    },
    setup: {
      cells: '{{cells}} cells',
      cellsRange: '{{min}}–{{max}} cells',
      expand: 'Expand setup',
      collapse: 'Collapse setup',
    },
    checks: {
      title: 'Verification checks',
      flaggedOf: '{{flagged}} of {{total}} flagged',
      allPass: 'all {{total}} pass',
      empty: 'No completed runs to check.',
      hidePassing_one: 'Hide {{count}} passing check',
      hidePassing_other: 'Hide {{count}} passing checks',
      showPassing_one: 'Show {{count}} passing check',
      showPassing_other: 'Show {{count}} passing checks',
      openFlagged: '{{detail}} →',
      label: {
        feaConvergence: 'Requested load steps completed',
        feaVerification: 'Structural verification',
        feaEquilibrium: 'Load and reaction equilibrium',
        residuals: 'Residual convergence',
        guard: 'Physics guards',
        massBalance: 'Mass balance',
        energyBalance: 'Energy balance',
        bounding: 'Solver limit corrections',
        anchor: 'Independent hand-calculation comparison',
        yPlus: 'Wall y⁺ distribution',
        meshIndependence: 'Mesh independence',
      },
      state: {
        pass: 'pass',
        warn: 'flagged',
        fail: 'failed',
        notRun: 'not run',
        notApplicable: 'does not apply',
      },
      detail: {
        ratio: '{{n}}/{{total}}',
        runs_one: '{{count}} run',
        runs_other: '{{count}} runs',
        worstPct: 'worst {{pct}}',
        range: '{{min}}–{{max}}',
        none: 'none',
        notRun: 'not run',
        notReported: 'not reported',
        notApplicable: 'does not apply to this run',
        residualTarget: '{{eq}} target {{target}}, reached {{achieved}}',
        residualTargetTooltip:
          'The target is a configured solver stopping threshold for this equation. “Reached” is the last recorded residual. A completed run may exhaust its iteration budget without meeting the target; a stopped or diverged run is not a verified result.',
        residualNoTargetTooltip:
          'This is the last recorded residual. No stopping target was reported for this equation, so it cannot be judged against a threshold. A stopped or diverged run is not a verified result.',
        residualNoTarget: '{{eq}} reached {{achieved}} — no target on the wire',
        anchorWorst: '{{pct}}',
        anchorCoverage: '{{n}}/{{total}} compared',
        gci: 'GCI {{pct}}%',
      },
    },
    anchors: {
      title: 'Results & balances',
      subtitle: "deviation only — the working lives in each run's hand calc",
      vsHand: 'vs hand',
      openRun: 'hand calc →',
      against: 'Deviations are against {{anchor}}.',
      empty: 'No hand-calc anchor applies to this template.',
    },
    response: {
      title: 'Response',
      quantity: 'Quantity',
      sweepHeading: 'Sweep response: ',
      vs: 'vs {{axis}}',
      pointsOf: '{{n}} of {{total}} points',
      empty:
        'Nothing varies across the runs — a single point has no response curve.',
      extremes: 'Extremes',
      min: 'min',
      max: 'max',
      missing_one: '{{count}} point missing — {{dps}} did not complete.',
      missing_other: '{{count}} points missing — {{dps}} did not complete.',
      unreported_one:
        '{{count}} run completed without reporting this output — {{dps}}.',
      unplaced_one:
        '{{count}} run has no value for the swept input and is not plotted — {{dps}}.',
      unplaced_other:
        '{{count}} runs have no value for the swept input and are not plotted — {{dps}}.',
      unreported_other:
        '{{count}} runs completed without reporting this output — {{dps}}.',
      whyFailed: 'Why they failed →',
    },
    runHealth: {
      completion: '{{n}} / {{total}} runs completed',
      medianRuntime: 'Median runtime',
      p95Runtime: '95th percentile runtime',
      convergence: 'Convergence reported as reached',
      convergenceHelp:
        'Recorded solver convergence, separate from run completion and physical verification.',
      notConverged: '{{n}} completed runs reported convergence not reached.',
      convergenceMissing: '{{n}} completed runs did not report convergence.',
      title: 'Run health',
      progressLabel: 'Share of runs finished',
      oneCause_one: 'The failure has one cause.',
      oneCause_other:
        'All {{count}} failures share one cause. Fixing it recovers {{count}} points of the sweep.',
      manyCauses: '{{groups}} distinct causes across {{count}} failures.',
      openFailures: 'Open the failure group →',
    },
    points: {
      title: 'Design points',
      fullTable: 'Full table with all inputs →',
      run: 'Run',
      status: 'Status',
      deltaVsBest: 'Δ vs best',
      checks: 'Checks',
      checkStateHelp:
        'Not run means an applicable verification produced no record. Does not apply means this solver does not use that check.',
      checkNotRun: 'Not run — no verification record',
      notSet: 'Not set or not reported',
      runtime: 'Runtime',
      open: 'open →',
      openError: 'error →',
      best: 'best',
    },
    fields: {
      title: 'Fields · {{dp}}',
      titleBest: 'Fields · {{dp}} (best run)',
      openViewer: 'Open viewer →',
    },
  },
  feaVerification: {
    axisLabel: 'Check',
    title: 'Structural self-consistency',
    hint: 'Scores use recorded FEA evidence only. Missing engineering limits remain unavailable.',
    unavailable: 'Unavailable',
    notEnoughAxes:
      'Fewer than three structural checks have recorded evidence, so there is no defensible radar polygon yet.',
    criterionLabel: 'Criterion',
    evidenceLabel: 'Evidence',
    scoreLabel: 'Score',
    safetyFactor: 'FoS {{value}}',
    distribution: 'Scored axes',
    worst: 'worst',
    median: 'median',
    best: 'best',
    evidence: {
      converged: 'converged',
      diverged: 'diverged',
      closed: 'all contacts closed',
      opened: 'contact opened',
    },
    axes: {
      solverCompletion: 'Solver completion',
      reactionEquilibrium: 'Reaction equilibrium',
      yieldMargin: 'Yield margin',
      ultimateMargin: 'Ultimate margin',
      displacementPlausibility: 'Displacement plausibility',
      contactIntegrity: 'Contact integrity',
    },
    criteria: {
      solverCompletion: 'binary',
      reactionEquilibrium: '±1% balance',
      yieldMargin: 'FoS 1.0 → 1.5',
      ultimateMargin: 'FoS 1.0 → 1.5',
      displacementPlausibility: 'requires a displacement limit',
      contactIntegrity: 'binary',
    },
    tooltips: {
      solverCompletion:
        '100% when the solver records convergence; 0% when it records divergence.',
      reactionEquilibrium:
        'Compares the resultant applied load with fixture reactions. Hidden from the radar until both are recorded.',
      yieldMargin:
        '0% at the yield limit (FoS 1.0), rising linearly to 100% at FoS 1.5. Values below 1.0 remain 0%.',
      ultimateMargin:
        '0% at the ultimate limit (FoS 1.0), rising linearly to 100% at FoS 1.5. Values below 1.0 remain 0%.',
      displacementPlausibility:
        'Compares peak displacement with a user-declared allowable displacement. Hidden until that limit exists.',
      contactIntegrity:
        '100% when every measured contact remains closed; 0% when any contact opens.',
    },
  },
} as const;
